const cSeparator = /[^\d+.-]+/;
function parseSafeFloat(text, fallback = 0) {
  try {
    return Number.parseFloat(text);
  } catch (_unused) {
    return fallback;
  }
}
function parseSafeFloats(text, fallback = 0) {
  return text.split(cSeparator).filter(Boolean).map(value => parseSafeFloat(value, fallback));
}
function toSafeString(elements) {
  return `[${elements.join(',')}]`;
}

function toCamelCase(text) {
  if (text.length > 0) {
    return text.slice(0, 1).toLocaleUpperCase() + text.slice(1);
  }
  return text;
}

const EasingFunctions = {
  linear: t => t,
  quadratic: t => t * t,
  cubic: t => t * t * t,
  quartric: t => t * t * t * t,
  quintic: t => t * t * t * t * t,
  sine: t => 1 - Math.cos(t * Math.PI / 2),
  exponential: t => Math.pow(2, 10 * (t - 1)),
  circle: t => 1 - Math.sqrt(1 - t * t),
  back: t => {
    const s = 1.70158;
    return t * t * ((s + 1) * t - s);
  },
  elastic: t => 1 - Math.pow(Math.cos(t * Math.PI / 2), 3) * Math.cos(t * Math.PI),
  bounce: t => {
    if (t < 1 / 2.75) {
      return 7.5625 * t * t;
    }
    if (t < 2 / 2.75) {
      const _t = t - 1.5 / 2.75;
      return 7.5625 * _t * _t + 0.75;
    }
    if (t < 2.5 / 2.75) {
      const _t2 = t - 2.25 / 2.75;
      return 7.5625 * _t2 * _t2 + 0.9375;
    }
    const t2 = t - 2.625 / 2.75;
    return 7.5625 * t2 * t2 + 0.984375;
  }
};
const EasingModes = {
  in: easing => {
    return easing;
  },
  out: easing => {
    return t => 1 - easing(1 - t);
  },
  inOut: easing => {
    return t => {
      if (t < 0.5) {
        return easing(t * 2) / 2;
      }
      return 1 - easing((1 - t) * 2) / 2;
    };
  }
};

/* eslint-disable no-promise-executor-return */
function sleep(durationInSeconds) {
  return new Promise(resolve => setTimeout(resolve, Math.round(durationInSeconds * 1000)));
}

//stackoverflow.com/a/54335525
function* sequence(start, end, step = 1) {
  let state = start;
  while (state < end) {
    yield state;
    state += step;
  }
  return;
}

const EPSILON = 0.000001; // chosen from gl-matrix

function equalsTolerance(a, b, tolerance = EPSILON) {
  return Math.abs(a - b) < tolerance;
}

// taken from gl-matrix
function equalsAutoTolerance(a, b) {
  return Math.abs(a - b) <= EPSILON * Math.max(1, Math.abs(a), Math.abs(b));
}
function degreesToRadians(a) {
  return a * (Math.PI / 180);
}
function radiansToDegrees(a) {
  return a * (180 / Math.PI);
}
function clamp(a, min, max) {
  return a < min ? min : a > max ? max : a;
}

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

class EventEmitter {
  constructor() {
    _defineProperty(this, "listeners", []);
  }
  addListener(listener) {
    this.listeners.push(listener);
  }
  removeListener(listener) {
    this.listeners.splice(this.listeners.indexOf(listener), 1);
  }
  clear() {
    this.listeners.splice(0, this.listeners.length);
  }
  emit(event) {
    if (this.listeners.length === 0) return;
    // copy array before emitting event to ensure even if listener array is modified, everyone listening initially gets the event.
    // inspired by mrdoob's EventDispatcher
    this.listeners.slice(0).forEach(listener => {
      listener(event);
    });
  }
  get listenerCount() {
    return this.listeners.length;
  }
}

class Logger {
  static verbose(text) {
    this.onVerbose.emit(text);
  }
  static info(text) {
    this.onInfo.emit(text);
  }
  static warn(text) {
    this.onWarn.emit(text);
  }
  static error(text) {
    this.onError.emit(text);
  }
}
_defineProperty(Logger, "onVerbose", new EventEmitter());
_defineProperty(Logger, "onInfo", new EventEmitter());
_defineProperty(Logger, "onWarn", new EventEmitter());
_defineProperty(Logger, "onError", new EventEmitter());
(() => {
  const prefix = () => {
    return new Date().toLocaleTimeString().padStart(11, '0');
  };
  Logger.onVerbose.addListener(text => {
    console.log(prefix() + ` VERB:  ${text}`);
  });
  Logger.onInfo.addListener(text => {
    console.log(prefix() + ` INFO:  ${text}`);
  });
  Logger.onWarn.addListener(text => {
    console.warn(prefix() + ` WARN:  ${text}`);
  });
  Logger.onError.addListener(text => {
    console.error(prefix() + ` ERR:  ${text}`);
  });
})();

class Assert {
  static mustEqual(value, mustEqualThisValue, msg = '') {
    if (value !== mustEqualThisValue) {
      throw new Error(`failed assertion: ${value} must equal ${mustEqualThisValue}.  ${msg}`);
    }
  }
  static mustBeTrue(condition, msg = '') {
    if (!condition) {
      throw new Error(`failed assertion: ${msg}`);
    }
  }
  static mustBeDefined(variable, msg = '') {
    if (variable === undefined) {
      throw new Error(`failed assertion: variable must be defined ${msg}`);
    }
  }
}

// Purpose:
//  - stores the node graph

const createNode = ({
  graph,
  nodes,
  values,
  nodeTypeName,
  nodeConfiguration: _nodeConfiguration = {}
}) => {
  let nodeDefinition = undefined;
  if (nodes[nodeTypeName]) {
    nodeDefinition = nodes[nodeTypeName];
  }
  if (nodeDefinition === undefined) {
    throw new Error(`no registered node descriptions with the typeName ${nodeTypeName}`);
  }
  const node = nodeDefinition.nodeFactory(graph, _nodeConfiguration);
  node.inputs.forEach(socket => {
    if (socket.valueTypeName !== 'flow' && socket.value === undefined) {
      var _values$socket$valueT;
      socket.value = (_values$socket$valueT = values[socket.valueTypeName]) === null || _values$socket$valueT === void 0 ? void 0 : _values$socket$valueT.creator();
    }
  });
  return node;
};
const makeGraphApi = ({
  variables: _variables = {},
  customEvents: _customEvents = {},
  valuesTypeRegistry,
  dependencies: _dependencies = {}
}) => ({
  variables: _variables,
  customEvents: _customEvents,
  values: valuesTypeRegistry,
  getDependency: id => {
    const result = _dependencies[id];
    if (!result) console.error(`Dependency not found ${id}.  Did you register it?`);
    return result;
  }
});

const readInputFromSockets = (inputs, inputName, nodeTypeName) => {
  const inputSocket = inputs.find(socket => socket.name === inputName);
  if (inputSocket === undefined) {
    throw new Error(`can not find input socket with name ${inputName} on node of type ${nodeTypeName}`);
  }
  return inputSocket.value;
};
const writeOutputsToSocket = (outputs, outputName, value, nodeTypeName) => {
  const outputSocket = outputs.find(socket => socket.name === outputName);
  if (outputSocket === undefined) {
    throw new Error(`can not find output socket with name ${outputName} on node of type ${nodeTypeName}`);
  }
  outputSocket.value = value;
};

class Node {
  // public typeName: string;

  constructor(node) {
    _defineProperty(this, "readInput", inputName => {
      return readInputFromSockets(this.inputs, inputName, this.description.typeName);
    });
    _defineProperty(this, "writeOutput", (outputName, value) => {
      writeOutputsToSocket(this.outputs, outputName, value, this.description.typeName);
    });
    this.inputs = node.inputs;
    this.outputs = node.outputs;
    this.description = node.description;
    this.nodeType = node.nodeType;
    this.graph = node.graph;
    this.configuration = node.configuration;
    this.metadata = node.metadata || {};
  }
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

let NodeType;
(function (NodeType) {
  NodeType["Event"] = "Event";
  NodeType["Flow"] = "Flow";
  NodeType["Async"] = "Async";
  NodeType["Function"] = "Function";
})(NodeType || (NodeType = {}));
const isFlowNode = node => node.nodeType === NodeType.Flow;
const isEventNode = node => node.nodeType === NodeType.Event;
const isAsyncNode = node => node.nodeType === NodeType.Async;
const isFunctionNode = node => node.nodeType === NodeType.Function;
const makeNodeInstance = node => {
  const readInput = inputName => {
    return readInputFromSockets(node.inputs, inputName, node.description.typeName);
  };
  const writeOutput = (outputName, value) => {
    writeOutputsToSocket(node.outputs, outputName, value, node.description.typeName);
  };
  return _objectSpread2(_objectSpread2({}, node), {}, {
    readInput,
    writeOutput
  });
};

// async flow node with only a single flow input
class AsyncNode extends Node {
  constructor(description, graph, inputs = [], outputs = [], configuration = {}) {
    super({
      description: _objectSpread2(_objectSpread2({}, description), {}, {
        category: description.category
      }),
      inputs,
      outputs,
      graph,
      nodeType: NodeType.Async,
      configuration
    });

    // must have at least one input flow socket
    Assert.mustBeTrue(this.inputs.some(socket => socket.valueTypeName === 'flow'));

    // must have at least one output flow socket
    Assert.mustBeTrue(this.outputs.some(socket => socket.valueTypeName === 'flow'));
  }

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  triggered(engine, triggeringSocketName, finished) {
    throw new Error('not implemented');
  }
  dispose() {
    throw new Error('not implemented');
  }
}
class AsyncNode2 extends AsyncNode {
  constructor(props) {
    super(props.description, props.graph, props.inputs, props.outputs);
  }
}
class AsyncNodeInstance extends Node {
  constructor(node) {
    super(_objectSpread2(_objectSpread2({}, node), {}, {
      nodeType: NodeType.Async
    }));
    _defineProperty(this, "triggered", (engine, triggeringSocketName, finished) => {
      this.triggeredInner({
        read: this.readInput,
        write: this.writeOutput,
        commit: (outFlowname, fiberCompletedListener) => engine.commitToNewFiber(this, outFlowname, fiberCompletedListener),
        configuration: this.configuration,
        graph: this.graph,
        finished,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        triggeringSocketName
      });
    });
    _defineProperty(this, "dispose", () => {
      this.state = this.disposeInner({
        state: this.state,
        graph: this.graph
      });
    });
    this.triggeredInner = node.triggered;
    this.disposeInner = node.dispose;
    this.state = node.initialState;
  }
}

// no flow inputs, always evaluated on startup
class EventNode extends Node {
  constructor(description, graph, inputs = [], outputs = [], configuration = {}) {
    super(_objectSpread2(_objectSpread2({}, description), {}, {
      description: _objectSpread2(_objectSpread2({}, description), {}, {
        category: description.category
      }),
      inputs,
      outputs,
      graph,
      configuration,
      nodeType: NodeType.Event
    }));
    // no input flow sockets allowed.
    Assert.mustBeTrue(!this.inputs.some(socket => socket.valueTypeName === 'flow'));

    // must have at least one output flow socket
    Assert.mustBeTrue(this.outputs.some(socket => socket.valueTypeName === 'flow'));
  }

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  init(engine) {
    throw new Error('not implemented');
  }

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  dispose(engine) {
    throw new Error('not implemented');
  }
}
class EventNode2 extends EventNode {
  constructor(props) {
    super(props.description, props.graph, props.inputs, props.outputs, props.configuration);
  }
}
class EventNodeInstance extends Node {
  constructor(nodeProps) {
    super(_objectSpread2(_objectSpread2({}, nodeProps), {}, {
      nodeType: NodeType.Event
    }));
    _defineProperty(this, "init", engine => {
      this.state = this.initInner({
        read: this.readInput,
        write: this.writeOutput,
        state: this.state,
        outputSocketKeys: this.outputSocketKeys,
        commit: (outFlowname, fiberCompletedListener) => engine.commitToNewFiber(this, outFlowname, fiberCompletedListener),
        configuration: this.configuration,
        graph: this.graph
      });
    });
    this.initInner = nodeProps.init;
    this.disposeInner = nodeProps.dispose;
    this.state = nodeProps.initialState;
    this.outputSocketKeys = nodeProps.outputs.map(s => s.name);
  }
  dispose() {
    this.disposeInner({
      state: this.state,
      graph: this.graph
    });
  }
}

class FlowNode extends Node {
  constructor(description, graph, inputs = [], outputs = [], configuration = {}) {
    // determine if this is an eval node
    super({
      description: _objectSpread2(_objectSpread2({}, description), {}, {
        category: description.category
      }),
      inputs,
      outputs,
      graph,
      configuration,
      nodeType: NodeType.Flow
    });

    // must have at least one input flow socket
    Assert.mustBeTrue(this.inputs.some(socket => socket.valueTypeName === 'flow'));
  }

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  triggered(fiber, triggeringSocketName) {
    throw new Error('not implemented');
  }
}
class FlowNode2 extends FlowNode {
  constructor(props) {
    super(props.description, props.graph, props.inputs, props.outputs, props.configuration);
  }
}
class FlowNodeInstance extends Node {
  constructor(nodeProps) {
    super(_objectSpread2(_objectSpread2({}, nodeProps), {}, {
      nodeType: NodeType.Flow
    }));
    _defineProperty(this, "triggered", (fiber, triggeringSocketName) => {
      this.state = this.triggeredInner({
        commit: (outFlowName, fiberCompletedListener) => fiber.commit(this, outFlowName, fiberCompletedListener),
        read: this.readInput,
        write: this.writeOutput,
        graph: this.graph,
        state: this.state,
        configuration: this.configuration,
        outputSocketKeys: this.outputSocketKeys,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        triggeringSocketName
      });
    });
    this.triggeredInner = nodeProps.triggered;
    this.state = nodeProps.initialState;
    this.outputSocketKeys = nodeProps.outputs.map(s => s.name);
  }
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

const _excluded = ["in", "out", "exec", "category"];
class FunctionNode extends Node {
  constructor(description, graph, inputs = [], outputs = [], exec, configuration = {}) {
    super({
      description: _objectSpread2(_objectSpread2({}, description), {}, {
        category: description.category
      }),
      inputs,
      outputs,
      graph,
      configuration,
      nodeType: NodeType.Function
    });

    // must have no input flow sockets
    this.exec = exec;
    Assert.mustBeTrue(!this.inputs.some(socket => socket.valueTypeName === 'flow'));

    // must have no output flow sockets
    Assert.mustBeTrue(!this.outputs.some(socket => socket.valueTypeName === 'flow'));
  }
}
class FunctionNodeInstance extends Node {
  constructor(nodeProps) {
    super(_objectSpread2(_objectSpread2({}, nodeProps), {}, {
      nodeType: NodeType.Function
    }));
    _defineProperty(this, "exec", node => {
      this.execInner({
        read: name => readInputFromSockets(node.inputs, name, node.description.typeName),
        write: (name, value) => writeOutputsToSocket(node.outputs, name, value, node.description.typeName),
        configuration: this.configuration,
        graph: this.graph
      });
    });
    this.execInner = nodeProps.exec;
  }
}
const alpha = 'abcdefghijklmnop';
const getAlphabeticalKey = index => alpha[index];

/** Converts list of sockets specifying value type names to an ordeered list of sockets,
 */

function makeSocketsList(sockets, getKey) {
  if (!sockets || sockets.length === 0) return [];
  return sockets.map((x, i) => {
    if (typeof x === 'string') {
      return {
        key: getKey(i),
        valueType: x
      };
    }
    return {
      key: Object.keys(x)[0],
      valueType: x[Object.keys(x)[0]]
    };
  });
}
function makeInNOutFunctionDesc(_ref) {
  let {
      in: inputs,
      out,
      exec,
      category
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const inputSockets = makeSocketsList(inputs, getAlphabeticalKey);
  const outputKeyFunc = typeof out === 'string' || out.length > 1 ? () => 'result' : getAlphabeticalKey;
  const outList = typeof out === 'string' ? [out] : out;
  const outputSockets = makeSocketsList(outList, outputKeyFunc);
  const definition = makeFunctionNodeDefinition({
    typeName: rest.name,
    label: rest.label,
    in: () => inputSockets,
    out: () => outputSockets,
    category,
    exec: ({
      read,
      write
    }) => {
      const args = inputSockets.map(({
        key
      }) => read(key));
      const results = exec(...args);
      if (outputSockets.length === 1 && outputSockets[0].key === 'result') {
        write('result', results);
      } else {
        outputSockets.forEach(({
          key
        }) => {
          write(key, results[key]);
        });
      }
    }
  });
  return definition;
}

class Socket {
  constructor(valueTypeName, name, value = undefined, label = undefined, valueChoices // if not empty, value must be one of these.
  ) {
    _defineProperty(this, "links", []);
    this.valueTypeName = valueTypeName;
    this.name = name;
    this.value = value;
    this.label = label;
    this.valueChoices = valueChoices;
  }
}

const makeSocketFromDefinition = (key, {
  valueType,
  defaultValue,
  choices
}) => new Socket(valueType, key, defaultValue, undefined, choices);
const makeSocketsFromMap = (socketConfig, keys, configuration, graphApi) => {
  return keys.map(key => {
    const definition = socketConfig[key];
    if (typeof definition === 'string') {
      return new Socket(definition, key);
    }
    if (typeof definition === 'function') {
      const socketDef = definition(configuration, graphApi);
      return makeSocketFromDefinition(key, socketDef);
    }
    return makeSocketFromDefinition(key, definition);
  });
};
const makeSocketsFromArray = sockets => sockets.map(socket => {
  return new Socket(socket.valueType, socket.key, socket.defaultValue, undefined, socket.choices);
});
function makeOrGenerateSockets(socketConfigOrFactory, nodeConfig, graph) {
  // if sockets definition is dynamic, then use the node config to generate it;
  // otherwise, use the static definition
  if (typeof socketConfigOrFactory === 'function') {
    const socketsConfig = socketConfigOrFactory(nodeConfig, graph);
    return makeSocketsFromArray(socketsConfig);
  }
  return makeSocketsFromMap(socketConfigOrFactory, Object.keys(socketConfigOrFactory), nodeConfig, graph);
}
const makeCommonProps = (nodeType, {
  typeName,
  in: inputs,
  out,
  otherTypeNames: _otherTypeNames = [],
  category: _category = NodeCategory.None,
  configuration: nodeDefinitionConfiguration,
  helpDescription: _helpDescription = '',
  label: _label = ''
}, configuration, graph) => ({
  description: {
    typeName: typeName,
    configuration: nodeDefinitionConfiguration || {},
    category: _category,
    otherTypeNames: _otherTypeNames,
    helpDescription: _helpDescription,
    label: _label
  },
  nodeType: nodeType,
  inputs: makeOrGenerateSockets(inputs, configuration, graph),
  outputs: makeOrGenerateSockets(out, configuration, graph),
  configuration,
  graph
});

let NodeCategory;
(function (NodeCategory) {
  NodeCategory["Action"] = "Action";
  NodeCategory["Query"] = "Query";
  NodeCategory["Logic"] = "Logic";
  NodeCategory["Event"] = "Event";
  NodeCategory["Variable"] = "Variable";
  NodeCategory["Flow"] = "Flow";
  NodeCategory["Time"] = "Time";
  NodeCategory["None"] = "None";
  NodeCategory["Effect"] = "Effect";
})(NodeCategory || (NodeCategory = {}));

// HELPER FUNCTIONS

// helper function to not require you to define generics when creating a node def:
function makeFlowNodeDefinition(definition) {
  return _objectSpread2(_objectSpread2({}, definition), {}, {
    nodeFactory: (graph, config) => new FlowNodeInstance(_objectSpread2(_objectSpread2({}, makeCommonProps(NodeType.Flow, definition, config, graph)), {}, {
      initialState: definition.initialState,
      triggered: definition.triggered
    }))
  });
}
function makeAsyncNodeDefinition(definition) {
  return _objectSpread2(_objectSpread2({}, definition), {}, {
    nodeFactory: (graph, config) => new AsyncNodeInstance(_objectSpread2(_objectSpread2({}, makeCommonProps(NodeType.Async, definition, config, graph)), {}, {
      initialState: definition.initialState,
      triggered: definition.triggered,
      dispose: definition.dispose
    }))
  });
}

// helper function to not require you to define generics when creating a node def,
// and generates a factory for a node instance
function makeFunctionNodeDefinition(definition) {
  return _objectSpread2(_objectSpread2({}, definition), {}, {
    nodeFactory: (graph, nodeConfig) => new FunctionNodeInstance(_objectSpread2(_objectSpread2({}, makeCommonProps(NodeType.Function, definition, nodeConfig, graph)), {}, {
      exec: definition.exec
    }))
  });
}
function makeEventNodeDefinition(definition) {
  return _objectSpread2(_objectSpread2({}, definition), {}, {
    nodeFactory: (graph, config) => new EventNodeInstance(_objectSpread2(_objectSpread2({}, makeCommonProps(NodeType.Event, definition, config, graph)), {}, {
      initialState: definition.initialState,
      init: definition.init,
      dispose: definition.dispose
    }))
  });
}

class Link {
  constructor(nodeId = '', socketName = '') {
    _defineProperty(this, "_targetNode", undefined);
    _defineProperty(this, "_targetSocket", undefined);
    this.nodeId = nodeId;
    this.socketName = socketName;
  }
}

class ValueType {
  constructor(name, creator, deserialize, serialize, lerp) {
    this.name = name;
    this.creator = creator;
    this.deserialize = deserialize;
    this.serialize = serialize;
    this.lerp = lerp;
  }
}

class CustomEvent {
  constructor(id, name, parameters = []) {
    _defineProperty(this, "label", '');
    _defineProperty(this, "metadata", {});
    _defineProperty(this, "eventEmitter", new EventEmitter());
    this.id = id;
    this.name = name;
    this.parameters = parameters;
  }
}

class Variable {
  // this is updated on each change to the variable state.

  constructor(id, name, valueTypeName, initialValue // this is assumed to be properly deseriealized from a string.
  ) {
    _defineProperty(this, "label", '');
    _defineProperty(this, "metadata", {});
    _defineProperty(this, "version", 0);
    _defineProperty(this, "onChanged", new EventEmitter());
    this.id = id;
    this.name = name;
    this.valueTypeName = valueTypeName;
    this.initialValue = initialValue;
    this.value = this.initialValue;
  }
  get() {
    return this.value;
  }
  set(newValue) {
    if (newValue !== this.value) {
      this.value = newValue;
      this.version++;
      this.onChanged.emit(this);
    }
  }
}

function resolveSocketValue(engine, inputSocket) {
  // if it has no links, leave value on input socket alone.
  if (inputSocket.links.length === 0) {
    return 0;
  }
  const nodes = engine.nodes;
  const upstreamLink = inputSocket.links[0];
  // caching the target node + socket here increases engine performance by 8% on average.  This is a hotspot.
  if (upstreamLink._targetNode === undefined || upstreamLink._targetSocket === undefined) {
    Assert.mustBeTrue(inputSocket.links.length === 1);

    // if upstream node is an eval, we just return its last value.
    upstreamLink._targetNode = nodes[upstreamLink.nodeId];
    // what is inputSocket connected to?
    upstreamLink._targetSocket = upstreamLink._targetNode.outputs.find(socket => socket.name === upstreamLink.socketName);
    if (upstreamLink._targetSocket === undefined) {
      throw new Error(`can not find socket with the name ${upstreamLink.socketName}`);
    }
  }
  const upstreamNode = upstreamLink._targetNode;
  const upstreamOutputSocket = upstreamLink._targetSocket;

  // if upstream is a flow/event/async node, do not evaluate it rather just use its existing output socket values
  if (!isFunctionNode(upstreamNode)) {
    inputSocket.value = upstreamOutputSocket.value;
    return 0;
  }
  let executionSteps = 0;
  if (isFunctionNode(upstreamNode)) {
    // resolve all inputs for the upstream node (this is where the recursion happens)
    // TODO: This is a bit dangerous as if there are loops in the graph, this will blow up the stack
    for (const upstreamInputSocket of upstreamNode.inputs) {
      executionSteps += resolveSocketValue(engine, upstreamInputSocket);
    }
    engine.onNodeExecutionStart.emit(upstreamNode);
    upstreamNode.exec(upstreamNode);
    executionSteps++;
    engine.onNodeExecutionEnd.emit(upstreamNode);

    // get the output value we wanted.
    inputSocket.value = upstreamOutputSocket.value;
    return executionSteps;
  }
  return 0;
}

class Fiber {
  constructor(engine, nextEval, fiberCompletedListener = undefined) {
    _defineProperty(this, "fiberCompletedListenerStack", []);
    _defineProperty(this, "executionSteps", 0);
    this.engine = engine;
    this.nextEval = nextEval;
    this.nodes = engine.nodes;
    if (fiberCompletedListener !== undefined) {
      this.fiberCompletedListenerStack.push(fiberCompletedListener);
    }
  }

  // this is syncCommit.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  commit(node, outputSocketName, fiberCompletedListener = undefined) {
    Assert.mustBeTrue(isFlowNode(node));
    Assert.mustBeTrue(this.nextEval === null);
    const outputSocket = node.outputs.find(socket => socket.name === outputSocketName);
    if (outputSocket === undefined) {
      throw new Error(`can not find socket with the name ${outputSocketName}`);
    }
    if (outputSocket.links.length > 1) {
      throw new Error('invalid for an output flow socket to have multiple downstream links:' + `${node.description.typeName}.${outputSocket.name} has ${outputSocket.links.length} downlinks`);
    }
    if (outputSocket.links.length === 1) {
      const link = outputSocket.links[0];
      if (link === undefined) {
        throw new Error('link must be defined');
      }
      this.nextEval = link;
    }
    if (fiberCompletedListener !== undefined) {
      this.fiberCompletedListenerStack.push(fiberCompletedListener);
    }
  }

  // returns the number of new execution steps created as a result of this one step
  executeStep() {
    // pop the next node off the queue
    const link = this.nextEval;
    this.nextEval = null;

    // nothing waiting, thus go back and start to evaluate any callbacks, in stack order.
    if (link === null) {
      if (this.fiberCompletedListenerStack.length === 0) {
        return;
      }
      const awaitingCallback = this.fiberCompletedListenerStack.pop();
      if (awaitingCallback === undefined) {
        throw new Error('awaitingCallback is empty');
      }
      awaitingCallback();
      return;
    }
    const node = this.nodes[link.nodeId];
    node.inputs.forEach(inputSocket => {
      if (inputSocket.valueTypeName !== 'flow') {
        this.executionSteps += resolveSocketValue(this.engine, inputSocket);
      }
    });

    // first resolve all input values
    // flow socket is set to true for the one flowing in, while all others are set to false.
    this.engine.onNodeExecutionStart.emit(node);
    if (isAsyncNode(node)) {
      this.engine.asyncNodes.push(node);
      node.triggered(this.engine, link.socketName, () => {
        // remove from the list of pending async nodes
        const index = this.engine.asyncNodes.indexOf(node);
        this.engine.asyncNodes.splice(index, 1);
        this.engine.onNodeExecutionEnd.emit(node);
        this.executionSteps++;
      });
      return;
    }
    if (isFlowNode(node)) {
      node.triggered(this, link.socketName);
      this.engine.onNodeExecutionEnd.emit(node);
      this.executionSteps++;
      return;
    }
    throw new TypeError(`should not get here, unhandled node ${node.description.typeName}`);
  }
  isCompleted() {
    return this.fiberCompletedListenerStack.length === 0 && this.nextEval === null;
  }
}

class Engine {
  // tracking the next node+input socket to execute.

  constructor(nodes) {
    _defineProperty(this, "fiberQueue", []);
    _defineProperty(this, "asyncNodes", []);
    _defineProperty(this, "eventNodes", []);
    _defineProperty(this, "onNodeExecutionStart", new EventEmitter());
    _defineProperty(this, "onNodeExecutionEnd", new EventEmitter());
    _defineProperty(this, "executionSteps", 0);
    this.nodes = nodes;
    // collect all event nodes
    Object.values(nodes).forEach(node => {
      if (isEventNode(node)) {
        this.eventNodes.push(node);
      }
    });
    // init all event nodes at startup
    this.eventNodes.forEach(eventNode => {
      // evaluate input parameters
      eventNode.inputs.forEach(inputSocket => {
        Assert.mustBeTrue(inputSocket.valueTypeName !== 'flow');
        this.executionSteps += resolveSocketValue(this, inputSocket);
      });
      this.onNodeExecutionStart.emit(eventNode);
      eventNode.init(this);
      this.executionSteps++;
      this.onNodeExecutionEnd.emit(eventNode);
    });
  }
  dispose() {
    // dispose all, possibly in-progress, async nodes
    this.asyncNodes.forEach(asyncNode => asyncNode.dispose());

    // dispose all event nodes
    this.eventNodes.forEach(eventNode => eventNode.dispose(this));
  }

  // asyncCommit
  commitToNewFiber(node, outputFlowSocketName, fiberCompletedListener = undefined) {
    Assert.mustBeTrue(isEventNode(node) || isAsyncNode(node));
    const outputSocket = node.outputs.find(socket => socket.name === outputFlowSocketName);
    if (outputSocket === undefined) {
      throw new Error(`no socket with the name ${outputFlowSocketName}`);
    }
    if (outputSocket.links.length > 1) {
      throw new Error('invalid for an output flow socket to have multiple downstream links:' + `${node.description.typeName}.${outputSocket.name} has ${outputSocket.links.length} downlinks`);
    }
    if (outputSocket.links.length === 1) {
      const fiber = new Fiber(this, outputSocket.links[0], fiberCompletedListener);
      this.fiberQueue.push(fiber);
    }
  }

  // NOTE: This does not execute all if there are promises.
  executeAllSync(limitInSeconds = 100, limitInSteps = 100000000) {
    const startDateTime = Date.now();
    let elapsedSeconds = 0;
    let elapsedSteps = 0;
    while (elapsedSteps < limitInSteps && elapsedSeconds < limitInSeconds && this.fiberQueue.length > 0) {
      const currentFiber = this.fiberQueue[0];
      const startingFiberExecutionSteps = currentFiber.executionSteps;
      currentFiber.executeStep();
      elapsedSteps += currentFiber.executionSteps - startingFiberExecutionSteps;
      if (currentFiber.isCompleted()) {
        // remove first element
        this.fiberQueue.shift();
      }
      elapsedSeconds = (Date.now() - startDateTime) * 0.001;
    }
    this.executionSteps += elapsedSteps;
    return elapsedSteps;
  }
  async executeAllAsync(limitInSeconds = 100, limitInSteps = 100000000) {
    const startDateTime = Date.now();
    let elapsedSteps = 0;
    let elapsedTime = 0;
    let iterations = 0;
    do {
      if (iterations > 0) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(0);
      }
      elapsedSteps += this.executeAllSync(limitInSeconds - elapsedTime, limitInSteps - elapsedSteps);
      elapsedTime = (Date.now() - startDateTime) * 0.001;
      iterations += 1;
    } while ((this.asyncNodes.length > 0 || this.fiberQueue.length > 0) && elapsedTime < limitInSeconds && elapsedSteps < limitInSteps);
    return elapsedSteps;
  }
}

// Purpose:
//  - loads a node graph
function readGraphFromJSON({
  graphJson,
  nodes: nodesTypeRegistry,
  values: valuesTypeRegistry,
  dependencies
}) {
  var _graphJson$nodes;
  const graphName = (graphJson === null || graphJson === void 0 ? void 0 : graphJson.name) || '';
  const graphMetadata = (graphJson === null || graphJson === void 0 ? void 0 : graphJson.metadata) || {};
  let variables = {};
  let customEvents = {};
  if ('variables' in graphJson) {
    var _graphJson$variables;
    variables = readVariablesJSON(valuesTypeRegistry, (_graphJson$variables = graphJson.variables) !== null && _graphJson$variables !== void 0 ? _graphJson$variables : []);
  }
  if ('customEvents' in graphJson) {
    var _graphJson$customEven;
    customEvents = readCustomEventsJSON(valuesTypeRegistry, (_graphJson$customEven = graphJson.customEvents) !== null && _graphJson$customEven !== void 0 ? _graphJson$customEven : []);
  }
  const nodesJson = (_graphJson$nodes = graphJson === null || graphJson === void 0 ? void 0 : graphJson.nodes) !== null && _graphJson$nodes !== void 0 ? _graphJson$nodes : [];
  if (nodesJson.length === 0) {
    Logger.warn('readGraphFromJSON: no nodes specified');
  }
  const graphApi = makeGraphApi({
    valuesTypeRegistry,
    variables,
    customEvents,
    dependencies
  });
  const nodes = {};
  // create new BehaviorNode instances for each node in the json.
  for (let i = 0; i < nodesJson.length; i += 1) {
    const nodeJson = nodesJson[i];
    const node = readNodeJSON({
      graph: graphApi,
      nodes: nodesTypeRegistry,
      values: valuesTypeRegistry,
      nodeJson
    });
    const id = nodeJson.id;
    if (id in nodes) {
      throw new Error(`can not create new node with id ${id} as one with that id already exists.`);
    }
    nodes[id] = node;
  }

  // connect up the graph edges from BehaviorNode inputs to outputs.  This is required to follow execution
  Object.entries(nodes).forEach(([nodeId, node]) => {
    // initialize the inputs by resolving to the reference nodes.
    node.inputs.forEach(inputSocket => {
      inputSocket.links.forEach(link => {
        if (!(link.nodeId in nodes)) {
          throw new Error(`node '${node.description.typeName}' specifies an input '${inputSocket.name}' whose link goes to ` + `a nonexistent upstream node id: ${link.nodeId}`);
        }
        const upstreamNode = nodes[link.nodeId];
        const upstreamOutputSocket = upstreamNode.outputs.find(socket => socket.name === link.socketName);
        if (upstreamOutputSocket === undefined) {
          throw new Error(`node '${node.description.typeName}' specifies an input '${inputSocket.name}' whose link goes to ` + `a nonexistent output '${link.socketName}' on upstream node '${upstreamNode.description.typeName}'`);
        }

        // add, only if unique
        const upstreamLink = new Link(nodeId, inputSocket.name);
        if (upstreamOutputSocket.links.findIndex(value => value.nodeId == upstreamLink.nodeId && value.socketName == upstreamLink.socketName) < 0) {
          upstreamOutputSocket.links.push(upstreamLink);
        }
      });
    });
    node.outputs.forEach(outputSocket => {
      outputSocket.links.forEach(link => {
        if (!(link.nodeId in nodes)) {
          throw new Error(`node '${node.description.typeName}' specifies an output '${outputSocket.name}' whose link goes to ` + `a nonexistent downstream node id ${link.nodeId}`);
        }
        const downstreamNode = nodes[link.nodeId];
        const downstreamInputSocket = downstreamNode.inputs.find(socket => socket.name === link.socketName);
        if (downstreamInputSocket === undefined) {
          throw new Error(`node '${node.description.typeName}' specifies an output '${outputSocket.name}' whose link goes to ` + `a nonexistent input '${link.socketName}' on downstream node '${downstreamNode.description.typeName}'`);
        }

        // add, only if unique
        const downstreamLink = new Link(nodeId, outputSocket.name);
        if (downstreamInputSocket.links.findIndex(value => value.nodeId == downstreamLink.nodeId && value.socketName == downstreamLink.socketName) < 0) {
          downstreamInputSocket.links.push(downstreamLink);
        }
      });
    });
  });
  return {
    name: graphName,
    metadata: graphMetadata,
    nodes: nodes,
    customEvents,
    variables
  };
}
function readNodeJSON({
  graph,
  nodes,
  values,
  nodeJson
}) {
  var _nodeJson$label, _nodeJson$metadata;
  if (nodeJson.type === undefined) {
    throw new Error('readGraphFromJSON: no type for node');
  }
  const nodeName = nodeJson.type;
  const nodeConfigurationJson = nodeJson.configuration;
  const nodeConfiguration = {};
  if (nodeConfigurationJson !== undefined) {
    Object.keys(nodeConfigurationJson).forEach(key => {
      nodeConfiguration[key] = nodeConfigurationJson[key];
    });
  }
  const node = createNode({
    graph,
    nodes,
    values,
    nodeTypeName: nodeName,
    nodeConfiguration
  });
  node.label = (_nodeJson$label = nodeJson === null || nodeJson === void 0 ? void 0 : nodeJson.label) !== null && _nodeJson$label !== void 0 ? _nodeJson$label : node.label;
  node.metadata = (_nodeJson$metadata = nodeJson === null || nodeJson === void 0 ? void 0 : nodeJson.metadata) !== null && _nodeJson$metadata !== void 0 ? _nodeJson$metadata : node.metadata;
  if (nodeJson.parameters !== undefined) {
    readNodeParameterJSON(values, node, nodeJson.parameters);
  }
  if (nodeJson.flows !== undefined) {
    readNodeFlowsJSON(node, nodeJson.flows);
  }
  return node;
}
function readNodeParameterJSON(valuesRegistry, node, parametersJson) {
  node.inputs.forEach(socket => {
    if (!(socket.name in parametersJson)) {
      return;
    }
    const inputJson = parametersJson[socket.name];
    if ('value' in inputJson) {
      var _valuesRegistry$socke;
      // eslint-disable-next-line no-param-reassign
      socket.value = (_valuesRegistry$socke = valuesRegistry[socket.valueTypeName]) === null || _valuesRegistry$socke === void 0 ? void 0 : _valuesRegistry$socke.deserialize(inputJson.value);
    }
    if ('link' in inputJson) {
      const linkJson = inputJson.link;
      socket.links.push(new Link(linkJson.nodeId, linkJson.socket));
    }
  });

  // validate that there are no additional input sockets specified that were not read.
  for (const inputName in parametersJson) {
    const inputSocket = node.inputs.find(socket => socket.name === inputName);
    if (inputSocket === undefined) {
      throw new Error(`node '${node.description.typeName}' specifies an input '${inputName}' that doesn't exist on its node type, available inputs are: ${node.inputs.map(input => input.name).join(', ')}`);
    }
  }
}
function readNodeFlowsJSON(node, flowsJson) {
  node.outputs.forEach(socket => {
    if (socket.name in flowsJson) {
      const outputLinkJson = flowsJson[socket.name];
      socket.links.push(new Link(outputLinkJson.nodeId, outputLinkJson.socket));
    }
  });

  // validate that there are no additional input sockets specified that were not read.
  for (const outputName in flowsJson) {
    const outputSocket = node.outputs.find(socket => socket.name === outputName);
    if (outputSocket === undefined) {
      throw new Error(`node '${node.description.typeName}' specifies an output '${outputName}' that doesn't exist on its node type, available outputs are: ${node.outputs.map(output => output.name).join(', ')}`);
    }
  }
}
function readVariablesJSON(valuesRegistry, variablesJson) {
  const variables = {};
  for (let i = 0; i < variablesJson.length; i += 1) {
    var _valuesRegistry$varia, _variableJson$label, _variableJson$metadat;
    const variableJson = variablesJson[i];
    const variable = new Variable(variableJson.id, variableJson.name, variableJson.valueTypeName, (_valuesRegistry$varia = valuesRegistry[variableJson.valueTypeName]) === null || _valuesRegistry$varia === void 0 ? void 0 : _valuesRegistry$varia.deserialize(variableJson.initialValue));
    variable.label = (_variableJson$label = variableJson === null || variableJson === void 0 ? void 0 : variableJson.label) !== null && _variableJson$label !== void 0 ? _variableJson$label : variable.label;
    variable.metadata = (_variableJson$metadat = variableJson === null || variableJson === void 0 ? void 0 : variableJson.metadata) !== null && _variableJson$metadat !== void 0 ? _variableJson$metadat : variable.metadata;
    if (variableJson.id in variables) {
      throw new Error(`duplicate variable id ${variable.id}`);
    }
    variables[variableJson.id] = variable;
  }
  return variables;
}
function readCustomEventsJSON(valuesRegistry, customEventsJson) {
  const customEvents = {};
  for (let i = 0; i < customEventsJson.length; i += 1) {
    var _customEventJson$para, _customEventJson$labe, _customEventJson$meta;
    const customEventJson = customEventsJson[i];
    const parameters = [];
    ((_customEventJson$para = customEventJson.parameters) !== null && _customEventJson$para !== void 0 ? _customEventJson$para : []).forEach(parameterJson => {
      var _valuesRegistry$param;
      parameters.push(new Socket(parameterJson.valueTypeName, parameterJson.name, (_valuesRegistry$param = valuesRegistry[parameterJson.valueTypeName]) === null || _valuesRegistry$param === void 0 ? void 0 : _valuesRegistry$param.deserialize(parameterJson.defaultValue)));
    });
    const customEvent = new CustomEvent(customEventJson.id, customEventJson.name, parameters);
    customEvent.label = (_customEventJson$labe = customEventJson === null || customEventJson === void 0 ? void 0 : customEventJson.label) !== null && _customEventJson$labe !== void 0 ? _customEventJson$labe : customEvent.label;
    customEvent.metadata = (_customEventJson$meta = customEventJson === null || customEventJson === void 0 ? void 0 : customEventJson.metadata) !== null && _customEventJson$meta !== void 0 ? _customEventJson$meta : customEvent.metadata;
    if (customEvent.id in customEvents) {
      throw new Error(`duplicate variable id ${customEvent.id}`);
    }
    customEvents[customEvent.id] = customEvent;
  }
  return customEvents;
}

function writeGraphToJSON(graph, valuesRegistry) {
  const graphJson = {};
  if (Object.keys(graph.metadata).length > 0) {
    graphJson.metadata = graph.metadata;
  }

  // save custom events
  Object.values(graph.customEvents).forEach(customEvent => {
    const customEventJson = {
      name: customEvent.name,
      id: customEvent.id
    };
    if (customEvent.label.length > 0) {
      customEventJson.label = customEvent.label;
    }
    if (customEvent.parameters.length > 0) {
      const parametersJson = [];
      customEvent.parameters.forEach(parameter => {
        parametersJson.push({
          name: parameter.name,
          valueTypeName: parameter.valueTypeName,
          defaultValue: parameter.value
        });
      });
      customEventJson.parameters = parametersJson;
    }
    if (Object.keys(customEvent.metadata).length > 0) {
      customEventJson.metadata = customEvent.metadata;
    }
    if (graphJson.customEvents === undefined) {
      graphJson.customEvents = [];
    }
    graphJson.customEvents.push(customEventJson);
  });

  // save variables
  Object.values(graph.variables).forEach(variable => {
    var _valuesRegistry$varia;
    const variableJson = {
      valueTypeName: variable.valueTypeName,
      name: variable.name,
      id: variable.id,
      initialValue: (_valuesRegistry$varia = valuesRegistry[variable.valueTypeName]) === null || _valuesRegistry$varia === void 0 ? void 0 : _valuesRegistry$varia.serialize(variable.initialValue)
    };
    if (variable.label.length > 0) {
      variableJson.label = variable.label;
    }
    if (Object.keys(variable.metadata).length > 0) {
      variableJson.metadata = variable.metadata;
    }
    if (graphJson.variables === undefined) {
      graphJson.variables = [];
    }
    graphJson.variables.push(variableJson);
  });

  // save nodes
  Object.entries(graph.nodes).forEach(([id, node]) => {
    const nodeJson = {
      type: node.description.typeName,
      id
    };
    if (node.label && node.label.length > 0) {
      nodeJson.label = node.label;
    }
    if (Object.keys(node.metadata).length > 0) {
      nodeJson.metadata = node.metadata;
    }
    if (Object.keys(node.description.configuration).length > 0) {
      const configurationJson = {};
      Object.keys(node.configuration).forEach(key => {
        configurationJson[key] = node.configuration[key];
      });
      nodeJson.configuration = configurationJson;
    }
    const parametersJson = {};
    node.inputs.forEach(inputSocket => {
      if (inputSocket.valueTypeName === 'flow') return;
      let parameterJson = undefined;
      if (inputSocket.links.length === 0) {
        var _valuesRegistry$input;
        parameterJson = {
          value: (_valuesRegistry$input = valuesRegistry[inputSocket.valueTypeName]) === null || _valuesRegistry$input === void 0 ? void 0 : _valuesRegistry$input.serialize(inputSocket.value)
        };
      } else if (inputSocket.links.length === 1) {
        const link = inputSocket.links[0];
        parameterJson = {
          link: {
            nodeId: link.nodeId,
            socket: link.socketName
          }
        };
      } else {
        throw new Error(`should not get here, inputSocket.links.length = ${inputSocket.links.length} > 1`);
      }
      parametersJson[inputSocket.name] = parameterJson;
    });
    if (Object.keys(parametersJson).length > 0) {
      nodeJson.parameters = parametersJson;
    }
    const flowsJson = {};
    node.outputs.forEach(outputSocket => {
      if (outputSocket.valueTypeName !== 'flow') return;
      if (outputSocket.links.length === 0) return;
      const linkJson = {
        nodeId: outputSocket.links[0].nodeId,
        socket: outputSocket.links[0].socketName
      };
      flowsJson[outputSocket.name] = linkJson;
    });
    if (Object.keys(flowsJson).length > 0) {
      nodeJson.flows = flowsJson;
    }
    if (graphJson.nodes === undefined) {
      graphJson.nodes = [];
    }
    graphJson.nodes.push(nodeJson);
  });
  return graphJson;
}

function toChoices(valueChoices) {
  return valueChoices === null || valueChoices === void 0 ? void 0 : valueChoices.map(choice => {
    if (typeof choice === 'string') return {
      text: choice,
      value: choice
    };
    return choice;
  });
}
function writeNodeSpecsToJSON({
  values,
  nodes,
  dependencies
}) {
  const nodeSpecsJSON = [];

  // const graph = new Graph(registry);

  const graph = {
    values: values,
    customEvents: {},
    getDependency: id => dependencies[id],
    variables: {}
  };
  Object.keys(nodes).forEach(nodeTypeName => {
    const node = createNode({
      graph,
      nodes,
      values,
      nodeTypeName
    });
    const nodeSpecJSON = {
      type: nodeTypeName,
      category: node.description.category,
      label: node.description.label,
      inputs: [],
      outputs: [],
      configuration: []
    };
    node.inputs.forEach(inputSocket => {
      const valueType = inputSocket.valueTypeName === 'flow' ? undefined : values[inputSocket.valueTypeName];
      let defaultValue = inputSocket.value;
      if (valueType !== undefined) {
        defaultValue = valueType.serialize(defaultValue);
      }
      if (defaultValue === undefined && valueType !== undefined) {
        defaultValue = valueType.serialize(valueType.creator());
      }
      const socketSpecJSON = {
        name: inputSocket.name,
        valueType: inputSocket.valueTypeName,
        defaultValue,
        choices: toChoices(inputSocket.valueChoices)
      };
      nodeSpecJSON.inputs.push(socketSpecJSON);
    });
    node.outputs.forEach(outputSocket => {
      const socketSpecJSON = {
        name: outputSocket.name,
        valueType: outputSocket.valueTypeName
      };
      nodeSpecJSON.outputs.push(socketSpecJSON);
    });
    nodeSpecsJSON.push(nodeSpecJSON);
  });
  return nodeSpecsJSON;
}

function getNodeDescriptions(importWildcard) {
  return Object.values(importWildcard);
}
class NodeDescription {
  constructor(typeName, category, label = '', factory, otherTypeNames = [], helpDescription = '', configuration = {}) {
    this.typeName = typeName;
    this.category = category;
    this.label = label;
    this.otherTypeNames = otherTypeNames;
    this.helpDescription = helpDescription;
    this.configuration = configuration;
    this.nodeFactory = (graph, config) => factory(this, graph, config);
  }
}
class NodeDescription2 extends NodeDescription {
  constructor(properties) {
    super(properties.typeName, properties.category, properties.label, properties.factory, properties.otherTypeNames, properties.helpDescription, properties.configuration);
    this.properties = properties;
  }
}

const nodeTypeNameRegex = /^\w+(\/\w+)*$/;
const socketNameRegex = /^\w+$/;
function validateNodeRegistry({
  nodes,
  values
}) {
  const errorList = [];
  // const graph = new Graph(registry);
  const graph = makeGraphApi({
    valuesTypeRegistry: values,
    dependencies: {}
  });
  Object.keys(nodes).forEach(nodeTypeName => {
    const node = createNode({
      graph,
      nodes,
      values,
      nodeTypeName
    });

    // ensure node is registered correctly.
    if (node.description.typeName !== nodeTypeName) {
      var _node$description$oth;
      if (!((_node$description$oth = node.description.otherTypeNames) !== null && _node$description$oth !== void 0 && _node$description$oth.includes(nodeTypeName))) {
        errorList.push(`node with typeName '${node.description.typeName}' is registered under a different name '${nodeTypeName}'`);
      }
    }
    if (!nodeTypeNameRegex.test(node.description.typeName)) {
      errorList.push(`invalid node type name on node ${node.description.typeName}`);
    }
    node.inputs.forEach(socket => {
      if (!socketNameRegex.test(socket.name)) {
        errorList.push(`invalid socket name for input socket ${socket.name} on node ${node.description.typeName}`);
      }
      if (socket.valueTypeName === 'flow') {
        return;
      }
      const valueType = values[socket.valueTypeName];
      // check to ensure all value types are supported.
      if (valueType === undefined) {
        errorList.push(`node '${node.description.typeName}' has on input socket '${socket.name}' an unregistered value type '${socket.valueTypeName}'`);
      }
    });
    node.outputs.forEach(socket => {
      if (!socketNameRegex.test(socket.name)) {
        errorList.push(`invalid socket name for output socket ${socket.name} on node ${node.description.typeName}`);
      }
      if (socket.valueTypeName === 'flow') {
        return;
      }
      const valueType = values[socket.valueTypeName];
      // check to ensure all value types are supported.
      if (valueType === undefined) {
        errorList.push(`node '${node.description.typeName}' has on output socket '${socket.name}' an unregistered value type '${socket.valueTypeName}'`);
      }
    });
  });
  return errorList;
}

const valueTypeNameRegex = /^\w+$/;
function validateValueRegistry(values) {
  const errorList = [];
  Object.keys(values).forEach(valueTypeName => {
    if (!valueTypeNameRegex.test(valueTypeName)) {
      errorList.push(`invalid value type name ${valueTypeName}`);
    }
    const valueType = values[valueTypeName];
    const value = valueType === null || valueType === void 0 ? void 0 : valueType.creator();
    const serializedValue = valueType === null || valueType === void 0 ? void 0 : valueType.serialize(value);
    const deserializedValue = valueType === null || valueType === void 0 ? void 0 : valueType.deserialize(serializedValue);
    const reserializedValue = valueType === null || valueType === void 0 ? void 0 : valueType.serialize(deserializedValue);
    const redeserializedValue = valueType === null || valueType === void 0 ? void 0 : valueType.deserialize(reserializedValue);
    if (JSON.stringify(serializedValue) !== JSON.stringify(reserializedValue)) {
      errorList.push(`value type (${valueTypeName}) reserialization mismatch between ${JSON.stringify(serializedValue)} and ${JSON.stringify(reserializedValue)}`);
    }
    if (typeof deserializedValue !== 'bigint' && JSON.stringify(deserializedValue) !== JSON.stringify(redeserializedValue)) {
      errorList.push(`value type (${valueTypeName}) redeserialization mismatch between ${JSON.stringify(deserializedValue)} and ${JSON.stringify(redeserializedValue)}`);
    }
  });
  return errorList;
}

function validateRegistry({
  nodes,
  values
}) {
  const errorList = [];
  errorList.push(...validateValueRegistry(values), ...validateNodeRegistry({
    nodes,
    values
  }));
  return errorList;
}

function validateGraphAcyclic(nodes) {
  // apparently if you can topological sort, it is a DAG according to: https://stackoverflow.com/questions/4168/graph-serialization/4577#4577

  // instead of modifying the graph, I will use metadata to mark it in place.
  Object.values(nodes).forEach(node => {
    // eslint-disable-next-line no-param-reassign
    node.metadata['dag.marked'] = 'false';
  });

  // it appears that we can just keep trimming nodes whose input sockets have no connections.
  // if we can remove all nodes, that means that there are no cycles.

  const nodesToMark = [];
  do {
    // clear array: https://stackoverflow.com/a/1232046
    nodesToMark.length = 0;
    Object.values(nodes).forEach(node => {
      // ignore existing marked nodes.
      if (node.metadata['dag.marked'] === 'true') {
        return;
      }
      let inputsConnected = false;
      node.inputs.forEach(inputSocket => {
        inputSocket.links.forEach(link => {
          // is the other end marked?  If not, then it is still connected.
          if (nodes[link.nodeId].metadata['dag.marked'] === 'false') {
            inputsConnected = true;
          }
        });
      });
      if (!inputsConnected) {
        nodesToMark.push(node);
      }
    });
    nodesToMark.forEach(node => {
      // eslint-disable-next-line no-param-reassign
      node.metadata['dag.marked'] = 'true';
    });
  } while (nodesToMark.length > 0);
  const errorList = [];

  // output errors for each unmarked node
  // also remove the metadata related to DAG marking
  Object.values(nodes).forEach(node => {
    if (node.metadata['dag.marked'] === 'false') {
      errorList.push(`node ${node.description.typeName} is part of a cycle, not a directed acyclic graph`);
    }
    // eslint-disable-next-line no-param-reassign
    delete node.metadata['dag.marked'];
  });
  return errorList;
}

function validateGraphLinks(nodes) {
  const errorList = [];
  // for each node
  Object.values(nodes).forEach(node => {
    // for each input socket
    node.inputs.forEach(inputSocket => {
      // ensure that connected output sockets are the same type
      inputSocket.links.forEach(link => {
        // check if the node id is correct
        if (!(link.nodeId in nodes)) {
          errorList.push(`node ${node.description.typeName}.${inputSocket.name} has link using invalid nodeId: ${link.nodeId}`);
          return;
        }

        // check if the socketName is correct
        const upstreamNode = nodes[link.nodeId];
        const outputSocket = upstreamNode.outputs.find(socket => socket.name === link.socketName);
        if (outputSocket === undefined) {
          errorList.push(`node ${node.description.typeName}.${inputSocket.name} has link using a non-existent socket name: ` + `${link.socketName}, it can not be found on upstream output node: ${upstreamNode.description.typeName}`);
          return;
        }

        // check if the socket types align
        if (inputSocket.valueTypeName !== outputSocket.valueTypeName) {
          errorList.push(`type mismatch between ${node.description.typeName}.${inputSocket.name} [${inputSocket.valueTypeName}] ` + `and ${upstreamNode.description.typeName}.${outputSocket.name} [${outputSocket.valueTypeName}]`);
        }
      });
    });
  });
  return errorList;
}

function validateGraph(graph) {
  const errorList = [];
  errorList.push(...validateGraphAcyclic(graph.nodes), ...validateGraphLinks(graph.nodes));
  return errorList;
}

function getStringConversionsForValueType({
  values,
  valueTypeName
}) {
  const camelCaseValueTypeName = toCamelCase(valueTypeName);
  return [makeInNOutFunctionDesc({
    name: `math/to${camelCaseValueTypeName}/string`,
    label: `To ${camelCaseValueTypeName}`,
    in: ['string'],
    out: valueTypeName,
    exec: a => {
      var _values$valueTypeName;
      return (_values$valueTypeName = values[valueTypeName]) === null || _values$valueTypeName === void 0 ? void 0 : _values$valueTypeName.deserialize(a);
    }
  }), makeInNOutFunctionDesc({
    name: `math/toString/${valueTypeName}`,
    label: 'To String',
    in: [valueTypeName],
    out: 'string',
    exec: a => {
      var _values$valueTypeName2;
      return (_values$valueTypeName2 = values[valueTypeName]) === null || _values$valueTypeName2 === void 0 ? void 0 : _values$valueTypeName2.serialize(a);
    }
  })];
}

/* eslint-disable class-methods-use-this */
class DefaultLogger {
  verbose(text) {
    Logger.verbose(text);
  }
  info(text) {
    Logger.info(text);
  }
  warn(text) {
    Logger.warn(text);
  }
  error(text) {
    Logger.error(text);
  }
}

class ManualLifecycleEventEmitter {
  constructor() {
    _defineProperty(this, "startEvent", new EventEmitter());
    _defineProperty(this, "endEvent", new EventEmitter());
    _defineProperty(this, "tickEvent", new EventEmitter());
  }
}

class OnCustomEvent extends EventNode2 {
  constructor(description, graph, configuration) {
    const customEvent = graph.customEvents[configuration.customEventId] || new CustomEvent('-1', 'undefined');
    super({
      description,
      graph,
      outputs: [new Socket('flow', 'flow'), ...customEvent.parameters.map(parameter => new Socket(parameter.valueTypeName, parameter.name, parameter.value, parameter.label))],
      configuration
    });
    _defineProperty(this, "onCustomEvent", undefined);
    this.customEvent = customEvent;
  }
  init(engine) {
    Assert.mustBeTrue(this.onCustomEvent === undefined);
    this.onCustomEvent = parameters => {
      this.customEvent.parameters.forEach(parameterSocket => {
        if (!(parameterSocket.name in parameters)) {
          throw new Error(`parameters of custom event do not align with parameters of custom event node, missing ${parameterSocket.name}`);
        }
        this.writeOutput(parameterSocket.name, parameters[parameterSocket.name]);
      });
      engine.commitToNewFiber(this, 'flow');
    };
    this.customEvent.eventEmitter.addListener(this.onCustomEvent);
  }
  dispose(engine) {
    Assert.mustBeTrue(this.onCustomEvent !== undefined);
    if (this.onCustomEvent !== undefined) {
      this.customEvent.eventEmitter.removeListener(this.onCustomEvent);
    }
  }
}
_defineProperty(OnCustomEvent, "Description", new NodeDescription2({
  typeName: 'customEvent/onTriggered',
  category: 'Event',
  label: 'On Triggered',
  configuration: {
    customEventId: {
      valueType: 'string',
      defaultValue: '-1'
    }
  },
  factory: (description, graph, configuration) => new OnCustomEvent(description, graph, configuration)
}));

class TriggerCustomEvent extends FlowNode2 {
  constructor(description, graph, configuration) {
    const customEvent = graph.customEvents[configuration.customEventId] || new CustomEvent('-1', 'undefined');
    super({
      description,
      graph,
      inputs: [new Socket('flow', 'flow'), ...customEvent.parameters.map(parameter => new Socket(parameter.valueTypeName, parameter.name, parameter.value, parameter.label))],
      outputs: [new Socket('flow', 'flow')],
      configuration
    });
    this.customEvent = customEvent;
  }
  triggered(fiber, triggeringSocketName) {
    const parameters = {};
    this.customEvent.parameters.forEach(parameterSocket => {
      parameters[parameterSocket.name] = this.readInput(parameterSocket.name);
    });
    this.customEvent.eventEmitter.emit(parameters);
  }
}
_defineProperty(TriggerCustomEvent, "Description", new NodeDescription2({
  typeName: 'customEvent/trigger',
  category: 'Action',
  label: 'Trigger',
  configuration: {
    customEventId: {
      valueType: 'string',
      defaultValue: '-1'
    }
  },
  factory: (description, graph, configuration) => new TriggerCustomEvent(description, graph, configuration)
}));

class ExpectTrue extends FlowNode {
  constructor(description, graph) {
    super(description, graph, [new Socket('flow', 'flow'), new Socket('boolean', 'condition'), new Socket('string', 'description')], [new Socket('flow', 'flow')]);
  }
  triggered(fiber, triggeredSocketName) {
    Assert.mustBeTrue(this.readInput('condition'), this.readInput('description'));
    fiber.commit(this, 'flow');
  }
}
_defineProperty(ExpectTrue, "Description", new NodeDescription('debug/expectTrue', 'Action', 'Assert Expect True', (description, graph) => new ExpectTrue(description, graph)));

const loggerDependencyKey = 'loggger';
const Log = makeFlowNodeDefinition({
  typeName: 'debug/log',
  category: NodeCategory.Action,
  label: 'Debug Log',
  in: {
    flow: 'flow',
    text: 'string',
    severity: {
      valueType: 'string',
      defaultValue: 'info',
      choices: ['verbose', 'info', 'warning', 'error'],
      label: 'severity'
    }
  },
  out: {
    flow: 'flow'
  },
  initialState: undefined,
  triggered: ({
    read,
    commit,
    graph: {
      getDependency
    }
  }) => {
    const logger = getDependency(loggerDependencyKey);
    const text = read('text');
    switch (read('severity')) {
      case 'verbose':
        logger === null || logger === void 0 ? void 0 : logger.verbose(text);
        break;
      case 'info':
        logger === null || logger === void 0 ? void 0 : logger.info(text);
        break;
      case 'warning':
        logger === null || logger === void 0 ? void 0 : logger.warn(text);
        break;
      case 'error':
        logger === null || logger === void 0 ? void 0 : logger.error(text);
        break;
    }
    commit('flow');
  }
});

const Branch = makeFlowNodeDefinition({
  typeName: 'flow/branch',
  category: NodeCategory.Flow,
  label: 'Branch',
  helpDescription: "Checks the value of the 'condition' input and if true, executes the 'true' branch, otherwise it executes the 'false' branch.",
  in: {
    flow: 'flow',
    condition: 'boolean'
  },
  out: {
    true: 'flow',
    false: 'flow'
  },
  triggered: ({
    read,
    commit
  }) => {
    commit(read('condition') === true ? 'true' : 'false');
  },
  initialState: undefined
});

const Counter = makeFlowNodeDefinition({
  typeName: 'flow/counter',
  label: 'Counter',
  in: {
    flow: 'flow',
    reset: 'flow'
  },
  out: {
    flow: 'flow',
    count: 'integer'
  },
  initialState: {
    count: BigInt(0)
  },
  category: NodeCategory.Flow,
  triggered: ({
    commit,
    write,
    triggeringSocketName,
    state
  }) => {
    let count = state.count;
    switch (triggeringSocketName) {
      case 'flow':
        {
          count++;
          // through type enforcement, write and commit can only write to one of the keys of `out`
          write('count', count);
          commit('flow');
          break;
        }
      case 'reset':
        {
          count = BigInt(0);
          break;
        }
      default:
        throw new Error('should not get here');
    }

    // return updated state
    return {
      count
    };
  }
});

// ASYNC - asynchronous evaluation
// also called "delay"

class Delay extends AsyncNode {
  constructor(description, graph) {
    super(description, graph, [new Socket('flow', 'flow'), new Socket('float', 'duration', 1)], [new Socket('flow', 'flow')]);
    _defineProperty(this, "timeoutPending", false);
  }
  triggered(engine, triggeringSocketName, finished) {
    // if there is a valid timeout running, leave it.
    if (this.timeoutPending) {
      return;
    }

    // otherwise start it.
    this.timeoutPending = true;
    setTimeout(() => {
      // check if cancelled
      if (!this.timeoutPending) return;
      this.timeoutPending = false;
      engine.commitToNewFiber(this, 'flow');
      finished();
    }, this.readInput('duration') * 1000);
  }
  dispose() {
    this.timeoutPending = false;
  }
}
_defineProperty(Delay, "Description", new NodeDescription2({
  typeName: 'time/delay',
  otherTypeNames: ['flow/delay'],
  category: 'Time',
  label: 'Delay',
  factory: (description, graph) => new Delay(description, graph)
}));

// based on Unreal Engine Blueprint DoN node

const DoN = makeFlowNodeDefinition({
  typeName: 'flow/doN',
  label: 'DoN',
  category: NodeCategory.Flow,
  in: {
    flow: 'flow',
    n: {
      valueType: 'integer',
      defaultValue: 1
    },
    reset: 'flow'
  },
  out: {
    flow: 'flow',
    count: 'integer'
  },
  initialState: {
    count: 0
  },
  triggered: ({
    commit,
    read,
    write,
    triggeringSocketName,
    state
  }) => {
    if (triggeringSocketName === 'reset') {
      return {
        count: 0
      };
    }
    if (state.count < Number(read('n'))) {
      write('count', state.count);
      commit('flow');
      return {
        count: state.count + 1
      };
    }
    return state;
  }
});

// based on Unreal Engine Blueprint DoN node

const DoOnce = makeFlowNodeDefinition({
  typeName: 'flow/doOnce',
  label: 'DoOnce',
  category: NodeCategory.Flow,
  in: {
    flow: 'flow',
    reset: 'flow'
  },
  out: {
    flow: 'flow'
  },
  initialState: {
    firedOnce: false
  },
  triggered: ({
    commit,
    triggeringSocketName,
    state
  }) => {
    if (triggeringSocketName === 'reset') {
      return {
        firedOnce: false
      };
    }
    if (!state.firedOnce) {
      commit('flow');
      return {
        firedOnce: true
      };
    }
    return state;
  }
});

// as long as this continues to be triggered within the duration period, it will not fire.
// based lousy on https://www.npmjs.com/package/debounce

class Debounce extends AsyncNode {
  constructor(description, graph) {
    super(description, graph, [new Socket('flow', 'flow'), new Socket('float', 'waitDuration'), new Socket('flow', 'cancel')], [new Socket('flow', 'flow')]);
    _defineProperty(this, "triggerVersion", 0);
  }
  triggered(engine, triggeringSocketName, finished) {
    this.triggerVersion++;

    // if cancelling, just increment triggerVersion and do not set a timer. :)
    if (triggeringSocketName === 'cancel') {
      return;
    }
    const localTriggerCount = this.triggerVersion;
    setTimeout(() => {
      if (this.triggerVersion >= localTriggerCount) {
        // ignore this timer, as it isn't for the most recent trigger
        return;
      }
      engine.commitToNewFiber(this, 'flow');
      finished();
    }, this.readInput('waitDuration') * 1000);
  }
  dispose() {
    this.triggerVersion++; // equivalent to 'cancel' trigger behavior.
  }
}
_defineProperty(Debounce, "Description", new NodeDescription('flow/debounce', 'Flow', 'Debounce', (description, graph) => new Debounce(description, graph)));

const FlipFlop = makeFlowNodeDefinition({
  typeName: 'flow/flipFlop',
  category: NodeCategory.Flow,
  label: 'Flip Flop',
  in: {
    flow: 'flow'
  },
  out: {
    on: 'flow',
    off: 'flow',
    isOn: 'boolean'
  },
  initialState: {
    isOn: true
  },
  triggered: ({
    commit,
    write,
    state
  }) => {
    write('isOn', state.isOn);
    commit(state.isOn ? 'on' : 'off');
    return {
      isOn: !state.isOn
    };
  }
});

const ForLoop = makeFlowNodeDefinition({
  typeName: 'flow/forLoop',
  category: NodeCategory.Flow,
  label: 'For Loop',
  in: {
    flow: 'flow',
    startIndex: 'integer',
    endIndex: 'integer'
  },
  out: {
    loopBody: 'flow',
    index: 'integer',
    completed: 'flow'
  },
  initialState: undefined,
  triggered: ({
    read,
    write,
    commit
  }) => {
    const startIndex = read('startIndex');
    const endIndex = read('endIndex');
    const loopBodyIteration = i => {
      if (i < endIndex) {
        write('index', i);
        commit('loopBody', () => {
          loopBodyIteration(i + BigInt(1));
        });
      } else {
        commit('completed');
      }
    };
    loopBodyIteration(startIndex);
  }
});

// based on Unreal Engine Blueprint Gate node

const Gate = makeFlowNodeDefinition({
  typeName: 'flow/gate',
  label: 'Gate',
  category: NodeCategory.Flow,
  in: {
    flow: 'flow',
    open: 'flow',
    close: 'flow',
    toggle: 'flow',
    startClosed: 'boolean'
  },
  out: {
    flow: 'flow'
  },
  initialState: {
    isInitialized: false,
    isClosed: true
  },
  triggered: ({
    commit,
    read,
    triggeringSocketName,
    state
  }) => {
    let isClosed = state.isClosed;
    let isInitialized = state.isInitialized;
    if (!state.isInitialized) {
      isClosed = !!read('startClosed');
      isInitialized = true;
    }
    switch (triggeringSocketName) {
      case 'flow':
        if (!isClosed) {
          commit('flow');
        }
        break;
      case 'open':
        isClosed = false;
        break;
      case 'close':
        isClosed = true;
        break;
      case 'toggle':
        isClosed = !isClosed;
        break;
      default:
        throw new Error(`Unexpected triggering socket: ${triggeringSocketName}`);
    }
    return {
      isClosed,
      isInitialized
    };
  }
});

// https://docs.unrealengine.com/4.27/en-US/ProgrammingAndScripting/Blueprints/UserGuide/flow/

const MultiGate = makeFlowNodeDefinition({
  typeName: 'flow/multiGate',
  category: NodeCategory.Flow,
  label: 'MultiGate',
  in: {
    flow: 'flow',
    reset: 'flow',
    loop: 'boolean',
    startIndex: 'integer'
  },
  out: {
    1: 'flow',
    2: 'flow',
    3: 'flow'
  },
  initialState: {
    isInitialized: false,
    nextIndex: 0
  },
  triggered: ({
    state,
    commit,
    read,
    outputSocketKeys,
    triggeringSocketName
  }) => {
    let nextIndex = state.nextIndex;
    let isInitialized = state.isInitialized;
    if (!isInitialized) {
      nextIndex = Number(read('startIndex'));
      isInitialized = true;
    }
    if (read('loop')) {
      nextIndex = nextIndex % outputSocketKeys.length;
    }
    switch (triggeringSocketName) {
      case 'reset':
        {
          nextIndex = 0;
          return {
            isInitialized,
            nextIndex
          };
        }
      case 'flow':
        {
          if (0 <= nextIndex && nextIndex < outputSocketKeys.length) {
            const output = outputSocketKeys[nextIndex];
            commit(output);
          }
          nextIndex++;
          return {
            isInitialized,
            nextIndex
          };
        }
    }
    // these outputs are fired sequentially in an sync fashion but without delays.
    // Thus a promise is returned and it continually returns a promise until each of the sequences has been executed.
    const sequenceIteration = i => {
      if (i < outputSocketKeys.length) {
        const outputSocket = outputSocketKeys[i];
        commit(outputSocket, () => {
          sequenceIteration(i + 1);
        });
      }
    };
    sequenceIteration(0);
    return {
      isInitialized,
      nextIndex
    };
  }
});

// https://docs.unrealengine.com/4.27/en-US/ProgrammingAndScripting/Blueprints/UserGuide/flow/

const Sequence = makeFlowNodeDefinition({
  typeName: 'flow/sequence',
  label: 'Sequence',
  configuration: {
    numOutputs: {
      valueType: 'number'
    }
  },
  in: {
    flow: 'flow'
  },
  out: configuration => {
    const numOutputs = configuration.numOutputs;
    const sockets = [];
    for (let outputIndex = 1; outputIndex <= numOutputs; outputIndex++) {
      const key = `${outputIndex}`;
      sockets.push({
        key,
        valueType: 'flow'
      });
    }
    return sockets;
  },
  initialState: undefined,
  triggered: ({
    commit,
    outputSocketKeys
  }) => {
    // these outputs are fired sequentially in an sync fashion but without delays.
    // Thus a promise is returned and it continually returns a promise until each of the sequences has been executed.
    const sequenceIteration = i => {
      if (i < outputSocketKeys.length) {
        const outputKey = outputSocketKeys[i];
        // const outputSocket = this.outputs[i];
        commit(outputKey, () => {
          sequenceIteration(i + 1);
        });
      }
    };
    sequenceIteration(0);
  }
});

// based on the description here: https://blog.webdevsimplified.com/2022-03/debounce-vs-throttle/

class Throttle extends AsyncNode {
  constructor(description, graph) {
    super(description, graph, [new Socket('flow', 'flow'), new Socket('float', 'duration', 1), new Socket('flow', 'cancel')], [new Socket('flow', 'flow')]);
    _defineProperty(this, "triggerVersion", 0);
    _defineProperty(this, "timeoutPending", false);
  }
  triggered(engine, triggeringSocketName, finished) {
    // if cancelling, just increment triggerVersion and do not set a timer. :)
    if (triggeringSocketName === 'cancel') {
      if (this.timeoutPending) {
        this.triggerVersion++;
        this.timeoutPending = false;
      }
      return;
    }

    // if there is a valid timeout running, leave it.
    if (this.timeoutPending) {
      return;
    }

    // otherwise start it.
    this.triggerVersion++;
    const localTriggerCount = this.triggerVersion;
    this.timeoutPending = true;
    setTimeout(() => {
      if (this.triggerVersion !== localTriggerCount) {
        return;
      }
      Assert.mustBeTrue(this.timeoutPending);
      this.timeoutPending = false;
      engine.commitToNewFiber(this, 'flow');
      finished();
    }, this.readInput('duration') * 1000);
  }
  dispose() {
    this.triggerVersion++; // equivalent to 'cancel' trigger behavior.
    this.timeoutPending = false;
  }
}
_defineProperty(Throttle, "Description", new NodeDescription('flow/throttle', 'Flow', 'Throttle', (description, graph) => new Throttle(description, graph)));

// this is equivalent to Promise.all()
class WaitAll extends FlowNode {
  constructor(description, graph, numInputs) {
    const inputs = [];
    for (let i = 1; i <= numInputs; i++) {
      inputs.push(new Socket('flow', `${i}`));
    }
    super(description, graph, [...inputs, new Socket('flow', 'reset'), new Socket('boolean', 'autoReset')], [new Socket('flow', 'flow')]);
    _defineProperty(this, "isOn", true);
    _defineProperty(this, "triggeredMap", {});
    _defineProperty(this, "triggeredCount", 0);
    _defineProperty(this, "outputTriggered", false);
    this.numInputs = numInputs;
    this.reset();
  }
  reset() {
    for (let inputIndex = 1; inputIndex <= this.numInputs; inputIndex++) {
      this.triggeredMap[`${inputIndex}`] = false;
    }
    this.triggeredCount = 0;
    this.outputTriggered = false;
  }
  triggered(fiber, triggeringSocketName) {
    if (triggeringSocketName === 'reset') {
      this.reset();
      return;
    }
    if (this.triggeredMap[triggeringSocketName]) {
      return;
    }
    this.triggeredMap[triggeringSocketName] = true;
    this.triggeredCount++;

    // if a & b are triggered, first output!
    if (this.triggeredCount === this.numInputs && !this.outputTriggered) {
      fiber.commit(this, 'flow');
      this.outputTriggered = true;

      // auto-reset if required.
      if (this.readInput('autoReset') === true) {
        this.reset();
      }
    }
  }
}
_defineProperty(WaitAll, "Description", new NodeDescription2({
  typeName: 'flow/waitAll',
  category: 'Flow',
  label: 'WaitAll',
  configuration: {
    numInputs: {
      valueType: 'number',
      defaultValue: 3
    }
  },
  factory: (description, graph, configuration) => new WaitAll(description, graph, configuration.numInputs)
}));

const makeInitialState$2 = () => ({
  onStartEvent: undefined
});
const lifecycleEventEmitterDependencyKey = 'lifecycleEventEmitter';
const LifecycleOnStart = makeEventNodeDefinition({
  typeName: 'lifecycle/onStart',
  label: 'On Start',
  category: NodeCategory.Event,
  in: {},
  out: {
    flow: 'flow'
  },
  initialState: makeInitialState$2(),
  init: ({
    state,
    commit,
    graph: {
      getDependency
    }
  }) => {
    Assert.mustBeTrue(state.onStartEvent === undefined);
    const onStartEvent = () => {
      commit('flow');
    };
    const lifecycleEventEmitter = getDependency(lifecycleEventEmitterDependencyKey);
    lifecycleEventEmitter === null || lifecycleEventEmitter === void 0 ? void 0 : lifecycleEventEmitter.startEvent.addListener(onStartEvent);
    return {
      onStartEvent
    };
  },
  dispose: ({
    state: {
      onStartEvent
    },
    graph: {
      getDependency
    }
  }) => {
    Assert.mustBeTrue(onStartEvent !== undefined);
    const lifecycleEventEmitter = getDependency(lifecycleEventEmitterDependencyKey);
    if (onStartEvent) lifecycleEventEmitter === null || lifecycleEventEmitter === void 0 ? void 0 : lifecycleEventEmitter.startEvent.removeListener(onStartEvent);
    return {};
  }
});

// inspired by: https://docs.unrealengine.com/4.27/en-US/ProgrammingAndScripting/Blueprints/UserGuide/Events/

const makeInitialState$1 = () => ({
  onEndEvent: undefined
});
const LifecycleOnEnd = makeEventNodeDefinition({
  typeName: 'lifecycle/onEnd',
  label: 'On End',
  category: NodeCategory.Event,
  in: {},
  out: {
    flow: 'flow'
  },
  initialState: makeInitialState$1(),
  init: ({
    state,
    commit,
    graph: {
      getDependency
    }
  }) => {
    Assert.mustBeTrue(state.onEndEvent === undefined);
    const onEndEvent = () => {
      commit('flow');
    };
    const lifecycleEventEmitter = getDependency(lifecycleEventEmitterDependencyKey);
    lifecycleEventEmitter === null || lifecycleEventEmitter === void 0 ? void 0 : lifecycleEventEmitter.endEvent.addListener(onEndEvent);
    return {
      onEndEvent
    };
  },
  dispose: ({
    state: {
      onEndEvent
    },
    graph: {
      getDependency
    }
  }) => {
    Assert.mustBeTrue(onEndEvent !== undefined);
    const lifecycleEventEmitter = getDependency(lifecycleEventEmitterDependencyKey);
    if (onEndEvent) lifecycleEventEmitter === null || lifecycleEventEmitter === void 0 ? void 0 : lifecycleEventEmitter.endEvent.removeListener(onEndEvent);
    return {};
  }
});

const makeInitialState = () => ({
  onTickEvent: undefined
});
const LifecycleOnTick = makeEventNodeDefinition({
  typeName: 'lifecycle/onTick',
  label: 'On Tick',
  category: NodeCategory.Event,
  in: {},
  out: {
    flow: 'flow',
    deltaSeconds: 'float'
  },
  initialState: makeInitialState(),
  init: ({
    state,
    commit,
    write,
    graph: {
      getDependency
    }
  }) => {
    Assert.mustBeTrue(state.onTickEvent === undefined);
    let lastTickTime = Date.now();
    const onTickEvent = () => {
      const currentTime = Date.now();
      const deltaSeconds = (currentTime - lastTickTime) * 0.001;
      write('deltaSeconds', deltaSeconds);
      commit('flow');
      lastTickTime = currentTime;
    };
    const lifecycleEventEmitter = getDependency(lifecycleEventEmitterDependencyKey);
    lifecycleEventEmitter === null || lifecycleEventEmitter === void 0 ? void 0 : lifecycleEventEmitter.tickEvent.addListener(onTickEvent);
    return {
      onTickEvent
    };
  },
  dispose: ({
    state: {
      onTickEvent
    },
    graph: {
      getDependency
    }
  }) => {
    Assert.mustBeTrue(onTickEvent !== undefined);
    const lifecycleEventEmitter = getDependency(lifecycleEventEmitterDependencyKey);
    if (onTickEvent) lifecycleEventEmitter === null || lifecycleEventEmitter === void 0 ? void 0 : lifecycleEventEmitter.tickEvent.removeListener(onTickEvent);
    return {};
  }
});

const Constant$3 = makeInNOutFunctionDesc({
  name: 'math/boolean',
  label: 'Boolean',
  in: ['boolean'],
  out: 'boolean',
  exec: a => a
});
const And = makeInNOutFunctionDesc({
  name: 'math/and/boolean',
  label: '∧',
  in: ['boolean', 'boolean'],
  out: 'boolean',
  exec: (a, b) => a && b
});
const Or = makeInNOutFunctionDesc({
  name: 'math/or/boolean',
  label: '∨',
  in: ['boolean', 'boolean'],
  out: 'boolean',
  exec: (a, b) => a || b
});
const Not = makeInNOutFunctionDesc({
  name: 'math/negate/boolean',
  label: '¬',
  in: ['boolean'],
  out: 'boolean',
  exec: a => !a
});
const ToFloat$2 = makeInNOutFunctionDesc({
  name: 'math/toFloat/boolean',
  label: 'To Float',
  in: ['boolean'],
  out: 'float',
  exec: a => a ? 1 : 0
});
const Equal$3 = makeInNOutFunctionDesc({
  name: 'math/equal/boolean',
  label: '=',
  in: ['boolean', 'boolean'],
  out: 'boolean',
  exec: (a, b) => a === b
});
const toInteger = makeInNOutFunctionDesc({
  name: 'math/toInteger/boolean',
  label: 'To Integer',
  in: ['boolean'],
  out: 'integer',
  exec: a => a ? 1n : 0n
});

var BooleanNodes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Constant: Constant$3,
  And: And,
  Or: Or,
  Not: Not,
  ToFloat: ToFloat$2,
  Equal: Equal$3,
  toInteger: toInteger
});

const BooleanValue = new ValueType('boolean', () => false, value => typeof value === 'string' ? value.toLowerCase() === 'true' : value, value => value, (start, end, t) => t < 0.5 ? start : end);

// Unreal Engine Blueprint Float nodes: https://docs.unrealengine.com/4.27/en-US/BlueprintAPI/Math/Float/

const Constant$2 = makeInNOutFunctionDesc({
  name: 'math/float',
  label: 'Float',
  in: ['float'],
  out: 'float',
  exec: a => a
});
const Add$1 = makeInNOutFunctionDesc({
  name: 'math/add/float',
  label: '+',
  in: ['float', 'float'],
  out: 'float',
  exec: (a, b) => a + b
});
const Subtract$1 = makeInNOutFunctionDesc({
  name: 'math/subtract/float',
  label: '-',
  in: ['float', 'float'],
  out: 'float',
  exec: (a, b) => a - b
});
const Negate$1 = makeInNOutFunctionDesc({
  name: 'math/negate/float',
  label: '-',
  in: ['float'],
  out: 'float',
  exec: a => -a
});
const Multiply$1 = makeInNOutFunctionDesc({
  name: 'math/multiply/float',
  label: '×',
  in: ['float', 'float'],
  out: 'float',
  exec: (a, b) => a * b
});
const Divide$1 = makeInNOutFunctionDesc({
  name: 'math/divide/float',
  label: '÷',
  in: ['float', 'float'],
  out: 'float',
  exec: (a, b) => a / b
});
const Modulus$1 = makeInNOutFunctionDesc({
  name: 'math/modulus/float',
  label: 'MOD',
  in: ['float', 'float'],
  out: 'float',
  exec: (a, b) => a % b
});
const Power = makeInNOutFunctionDesc({
  name: 'math/pow/float',
  label: 'POW',
  in: ['float', 'float'],
  out: 'float',
  exec: Math.pow
});
const SquareRoot = makeInNOutFunctionDesc({
  name: 'math/sqrt/float',
  label: '√',
  in: ['float'],
  out: 'float',
  exec: Math.sqrt
});
const E = makeInNOutFunctionDesc({
  name: 'math/e/float',
  label: '𝑒',
  out: 'float',
  exec: () => Math.E
});
const Exp = makeInNOutFunctionDesc({
  name: 'math/exp/float',
  label: 'EXP',
  in: ['float'],
  out: 'float',
  exec: Math.exp
});
const Ln = makeInNOutFunctionDesc({
  name: 'math/ln/float',
  label: 'LN',
  in: ['float'],
  out: 'float',
  exec: Math.log
});
const Log2 = makeInNOutFunctionDesc({
  name: 'math/log2/float',
  label: 'LOG2',
  in: ['float'],
  out: 'float',
  exec: Math.log2
});
const Log10 = makeInNOutFunctionDesc({
  name: 'math/log10/float',
  label: 'LOG10',
  in: ['float'],
  out: 'float',
  exec: Math.log10
});
const PI = makeInNOutFunctionDesc({
  name: 'math/pi/float',
  label: 'π',
  out: 'float',
  exec: () => Math.PI
});
const Sin = makeInNOutFunctionDesc({
  name: 'math/sin/float',
  label: 'SIN',
  in: ['float'],
  out: 'float',
  exec: Math.sin
});
const Asin = makeInNOutFunctionDesc({
  name: 'math/asin/float',
  label: 'ASIN',
  in: ['float'],
  out: 'float',
  exec: Math.asin
});
const Cos = makeInNOutFunctionDesc({
  name: 'math/cos/float',
  label: 'COS',
  in: ['float'],
  out: 'float',
  exec: Math.cos
});
const Acos = makeInNOutFunctionDesc({
  name: 'math/acos/float',
  label: 'ACOS',
  in: ['float'],
  out: 'float',
  exec: Math.acos
});
const Tan = makeInNOutFunctionDesc({
  name: 'math/tan/float',
  label: 'TAN',
  in: ['float'],
  out: 'float',
  exec: Math.tan
});
const RadiansToDegrees = makeInNOutFunctionDesc({
  name: 'math/radiansToDegrees/float',
  label: 'To Degrees',
  in: ['float'],
  out: 'float',
  exec: radiansToDegrees
});
const DegreesToRadians = makeInNOutFunctionDesc({
  name: 'math/degreesToRadians/float',
  label: 'To Radians',
  in: ['float'],
  out: 'float',
  exec: degreesToRadians
});
const Atan = makeInNOutFunctionDesc({
  name: 'math/atan/float',
  label: 'ATAN',
  in: ['float'],
  out: 'float',
  exec: Math.atan
});
const Mix = makeInNOutFunctionDesc({
  name: 'math/mix/float',
  label: 'MIX',
  in: ['float', 'float', 'float'],
  out: 'float',
  exec: (a, b, t) => {
    const s = 1 - t;
    return a * s + b * t;
  }
});
const ToFloat$1 = makeInNOutFunctionDesc({
  name: 'math/toFloat/float',
  label: 'To Float',
  in: ['float'],
  out: 'float',
  exec: a => Number(a)
});
const Min$1 = makeInNOutFunctionDesc({
  name: 'math/min/float',
  label: 'MIN',
  in: ['float', 'float'],
  out: 'float',
  exec: (a, b) => Math.min(a, b) // TODO: can I jsut pass in Math.min?
});

const Max$1 = makeInNOutFunctionDesc({
  name: 'math/max/float',
  label: 'MAX',
  in: ['float', 'float'],
  out: 'float',
  exec: (a, b) => Math.max(a, b) // TODO: can I jsut pass in Math.max?
});

const Clamp$1 = makeInNOutFunctionDesc({
  name: 'math/clamp/float',
  label: 'CLAMP',
  in: ['float', 'float', 'float'],
  out: 'float',
  exec: (value, min, max) => value < min ? min : value > max ? max : value
});
const Abs$1 = makeInNOutFunctionDesc({
  name: 'math/abs/float',
  label: 'ABS',
  in: ['float'],
  out: 'float',
  exec: Math.abs
});
const Sign$1 = makeInNOutFunctionDesc({
  name: 'math/sign/float',
  label: 'SIGN',
  in: ['float'],
  out: 'float',
  exec: Math.sign
});
const Floor = makeInNOutFunctionDesc({
  name: 'math/floor/float',
  label: 'FLOOR',
  in: ['float'],
  out: 'float',
  exec: Math.floor
});
const Ceil = makeInNOutFunctionDesc({
  name: 'math/ceil/float',
  label: 'CEIL',
  in: ['float'],
  out: 'float',
  exec: Math.ceil
});
const Round = makeInNOutFunctionDesc({
  name: 'math/round/float',
  label: 'ROUND',
  in: ['float'],
  out: 'float',
  exec: Math.round
});
const Trunc = makeInNOutFunctionDesc({
  name: 'math/trunc/float',
  label: 'TRUNC',
  in: ['float'],
  out: 'float',
  exec: Math.trunc
});
const Random = makeInNOutFunctionDesc({
  name: 'math/random/float',
  label: 'RANDOM',
  out: 'float',
  exec: Math.random
});
const Equal$2 = makeInNOutFunctionDesc({
  name: 'math/equal/float',
  label: '=',
  in: ['float', 'float'],
  out: 'boolean',
  exec: (a, b) => a === b
});
const EqualTolerance = makeInNOutFunctionDesc({
  name: 'math/equalTolerance/float',
  label: '=',
  in: ['float', 'float', 'float'],
  out: 'boolean',
  exec: (a, b, tolerance) => equalsTolerance(a, b, tolerance)
});
const GreaterThan$1 = makeInNOutFunctionDesc({
  name: 'math/greaterThan/float',
  label: '>',
  in: ['float', 'float'],
  out: 'boolean',
  exec: (a, b) => a > b
});
const GreaterThanOrEqual$1 = makeInNOutFunctionDesc({
  name: 'math/greaterThanOrEqual/float',
  label: '≥',
  in: ['float', 'float'],
  out: 'boolean',
  exec: (a, b) => a >= b
});
const LessThan$1 = makeInNOutFunctionDesc({
  name: 'math/lessThan/float',
  label: '<',
  in: ['float', 'float'],
  out: 'boolean',
  exec: (a, b) => a < b
});
const LessThanOrEqual$1 = makeInNOutFunctionDesc({
  name: 'math/lessThanOrEqual/float',
  label: '≤',
  in: ['float', 'float'],
  out: 'boolean',
  exec: (a, b) => a <= b
});
const IsNaN = makeInNOutFunctionDesc({
  name: 'math/isNaN/float',
  label: 'isNaN',
  in: ['float'],
  out: 'boolean',
  exec: Number.isNaN
});
const IsInf = makeInNOutFunctionDesc({
  name: 'math/isInf/float',
  label: 'isInf',
  in: ['float'],
  out: 'boolean',
  exec: a => !Number.isFinite(a) && !Number.isNaN(a)
});

var FloatNodes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Constant: Constant$2,
  Add: Add$1,
  Subtract: Subtract$1,
  Negate: Negate$1,
  Multiply: Multiply$1,
  Divide: Divide$1,
  Modulus: Modulus$1,
  Power: Power,
  SquareRoot: SquareRoot,
  E: E,
  Exp: Exp,
  Ln: Ln,
  Log2: Log2,
  Log10: Log10,
  PI: PI,
  Sin: Sin,
  Asin: Asin,
  Cos: Cos,
  Acos: Acos,
  Tan: Tan,
  RadiansToDegrees: RadiansToDegrees,
  DegreesToRadians: DegreesToRadians,
  Atan: Atan,
  Mix: Mix,
  ToFloat: ToFloat$1,
  Min: Min$1,
  Max: Max$1,
  Clamp: Clamp$1,
  Abs: Abs$1,
  Sign: Sign$1,
  Floor: Floor,
  Ceil: Ceil,
  Round: Round,
  Trunc: Trunc,
  Random: Random,
  Equal: Equal$2,
  EqualTolerance: EqualTolerance,
  GreaterThan: GreaterThan$1,
  GreaterThanOrEqual: GreaterThanOrEqual$1,
  LessThan: LessThan$1,
  LessThanOrEqual: LessThanOrEqual$1,
  IsNaN: IsNaN,
  IsInf: IsInf
});

const FloatValue = new ValueType('float', () => 0, value => typeof value === 'string' ? parseSafeFloat(value, 0) : value, value => value, (start, end, t) => start * (1 - t) + end * t);

// Unreal Engine Integer Blueprints API: https://docs.unrealengine.com/4.27/en-US/BlueprintAPI/Math/Integer/

const Constant$1 = makeInNOutFunctionDesc({
  name: 'math/integer',
  label: 'Integer',
  in: ['integer'],
  out: 'integer',
  exec: a => a
});
const Add = makeInNOutFunctionDesc({
  name: 'math/add/integer',
  label: '+',
  in: ['integer', 'integer'],
  out: 'integer',
  exec: (a, b) => a + b
});
const Subtract = makeInNOutFunctionDesc({
  name: 'math/subtract/integer',
  label: '-',
  in: ['integer', 'integer'],
  out: 'integer',
  exec: (a, b) => a - b
});
const Negate = makeInNOutFunctionDesc({
  name: 'math/negate/integer',
  label: '-',
  in: ['integer'],
  out: 'integer',
  exec: a => -a
});
const Multiply = makeInNOutFunctionDesc({
  name: 'math/multiply/integer',
  label: '×',
  in: ['integer', 'integer'],
  out: 'integer',
  exec: (a, b) => a * b
});
const Divide = makeInNOutFunctionDesc({
  name: 'math/divide/integer',
  label: '÷',
  in: ['integer', 'integer'],
  out: 'integer',
  exec: (a, b) => a / b
});
const Modulus = makeInNOutFunctionDesc({
  name: 'math/modulus/integer',
  label: 'MOD',
  in: ['integer', 'integer'],
  out: 'integer',
  exec: (a, b) => a % b
});
const ToFloat = makeInNOutFunctionDesc({
  name: 'math/toFloat/integer',
  label: 'To Float',
  in: ['integer'],
  out: 'float',
  exec: a => Number(a)
});
const Min = makeInNOutFunctionDesc({
  name: 'math/min/integer',
  label: 'MIN',
  in: ['integer', 'integer'],
  out: 'integer',
  exec: (a, b) => a > b ? b : a
});
const Max = makeInNOutFunctionDesc({
  name: 'math/max/integer',
  label: 'MAX',
  in: ['integer', 'integer'],
  out: 'integer',
  exec: (a, b) => a > b ? a : b
});
const Clamp = makeInNOutFunctionDesc({
  name: 'math/clamp/integer',
  label: 'CLAMP',
  in: [{
    value: 'integer'
  }, {
    min: 'integer'
  }, {
    max: 'integer'
  }],
  out: 'integer',
  exec: (value, min, max) => value < min ? min : value > max ? max : value
});
const Abs = makeInNOutFunctionDesc({
  name: 'math/abs/integer',
  label: 'ABS',
  in: ['integer'],
  out: 'integer',
  exec: a => a < BigInt(0) ? -a : a
});
const Sign = makeInNOutFunctionDesc({
  name: 'math/sign/integer',
  label: 'SIGN',
  in: ['integer'],
  out: 'integer',
  exec: a => BigInt(a < 0 ? -1 : a > 0 ? 1 : 0)
});
const Equal$1 = makeInNOutFunctionDesc({
  name: 'math/equal/integer',
  label: '=',
  in: ['integer', 'integer'],
  out: 'boolean',
  exec: (a, b) => a === b
});
const GreaterThan = makeInNOutFunctionDesc({
  name: 'math/greaterThan/integer',
  label: '>',
  in: ['integer', 'integer'],
  out: 'boolean',
  exec: (a, b) => a > b
});
const GreaterThanOrEqual = makeInNOutFunctionDesc({
  name: 'math/greaterThanOrEqual/integer',
  label: '≥',
  in: ['integer', 'integer'],
  out: 'boolean',
  exec: (a, b) => a >= b
});
const LessThan = makeInNOutFunctionDesc({
  name: 'math/lessThan/integer',
  label: '<',
  in: ['integer', 'integer'],
  out: 'boolean',
  exec: (a, b) => a < b
});
const LessThanOrEqual = makeInNOutFunctionDesc({
  name: 'math/lessThanOrEqual/integer',
  label: '≤',
  in: ['integer', 'integer'],
  out: 'boolean',
  exec: (a, b) => a <= b
});
const toBoolean = makeInNOutFunctionDesc({
  name: 'math/toBoolean/integer',
  label: 'To Boolean',
  in: ['integer'],
  out: 'boolean',
  exec: a => a !== 0n
});

var IntegerNodes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Constant: Constant$1,
  Add: Add,
  Subtract: Subtract,
  Negate: Negate,
  Multiply: Multiply,
  Divide: Divide,
  Modulus: Modulus,
  ToFloat: ToFloat,
  Min: Min,
  Max: Max,
  Clamp: Clamp,
  Abs: Abs,
  Sign: Sign,
  Equal: Equal$1,
  GreaterThan: GreaterThan,
  GreaterThanOrEqual: GreaterThanOrEqual,
  LessThan: LessThan,
  LessThanOrEqual: LessThanOrEqual,
  toBoolean: toBoolean
});

const IntegerValue = new ValueType('integer', () => BigInt(0), value => BigInt(value), value => Number.MIN_SAFE_INTEGER <= value && value <= Number.MAX_SAFE_INTEGER ? Number(value) : value.toString(),
// prefer string to ensure full range is covered

(start, end, t) => BigInt(Number(start) * (1 - t) + Number(end) * t));

const Constant = makeInNOutFunctionDesc({
  name: 'logic/string',
  label: 'String',
  in: ['string'],
  out: 'string',
  exec: a => a
});
const Concat = makeInNOutFunctionDesc({
  name: 'logic/concat/string',
  label: 'Concat',
  in: ['string', 'string'],
  out: 'string',
  exec: (a, b) => a.concat(b)
});
const Includes = makeInNOutFunctionDesc({
  name: 'logic/includes/string',
  label: 'Includes',
  in: ['string', 'string'],
  out: 'boolean',
  exec: (a, b) => a.includes(b)
});
const Length = makeInNOutFunctionDesc({
  name: 'logic/length/string',
  label: 'Length',
  in: ['string'],
  out: 'integer',
  exec: a => BigInt(a.length)
});
const Equal = makeInNOutFunctionDesc({
  name: 'math/equal/string',
  label: '=',
  in: ['string', 'string'],
  out: 'boolean',
  exec: (a, b) => a === b
});

var StringNodes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Constant: Constant,
  Concat: Concat,
  Includes: Includes,
  Length: Length,
  Equal: Equal
});

const StringValue = new ValueType('string', () => '', value => value, value => value, (start, end, t) => t < 0.5 ? start : end);

const VariableSet = makeFlowNodeDefinition({
  typeName: 'variable/set',
  category: NodeCategory.Action,
  label: 'Set',
  configuration: {
    variableId: {
      valueType: 'number'
    }
  },
  in: (configuration, graph) => {
    const variable = graph.variables[configuration.variableId] || new Variable('-1', 'undefined', 'string', '');
    const sockets = [{
      key: 'flow',
      valueType: 'flow'
    }, {
      key: 'value',
      valueType: variable.valueTypeName,
      label: variable.name
    }];
    return sockets;
  },
  initialState: undefined,
  out: {
    flow: 'flow'
  },
  triggered: ({
    read,
    commit,
    graph: {
      variables
    },
    configuration
  }) => {
    const variable = variables[configuration.variableId];
    if (!variable) return;
    variable.set(read('value'));
    commit('flow');
  }
});

const VariableGet = makeFunctionNodeDefinition({
  typeName: 'variable/get',
  category: NodeCategory.Query,
  label: 'Get',
  configuration: {
    variableId: {
      valueType: 'number'
    }
  },
  in: {},
  out: (configuration, graph) => {
    const variable = graph.variables[configuration.variableId] || new Variable('-1', 'undefined', 'string', '');
    const result = [{
      key: 'flow',
      valueType: 'flow'
    }, {
      key: 'value',
      valueType: variable.valueTypeName,
      label: variable.name
    }];
    return result;
  },
  exec: ({
    write,
    graph: {
      variables
    },
    configuration
  }) => {
    const variable = variables[configuration.variableId];
    if (!variable) return;
    write('value', variable.get());
  }
});

// https://docs.unrealengine.com/4.27/en-US/ProgrammingAndScripting/Blueprints/UserGuide/flow/

const SwitchOnInteger = makeFlowNodeDefinition({
  typeName: 'flow/switch/integer',
  label: 'Switch on Int',
  configuration: {
    numCases: {
      valueType: 'number'
    }
  },
  in: configuration => {
    const sockets = [];
    sockets.push({
      key: 'flow',
      valueType: 'flow'
    }, {
      key: 'selection',
      valueType: 'integer'
    });
    for (const index of sequence(1, configuration.numCases + 1)) {
      sockets.push({
        key: `${index}`,
        valueType: 'integer'
      });
    }
    return sockets;
  },
  out: configuration => {
    const sockets = [];
    sockets.push({
      key: 'default',
      valueType: 'flow'
    });
    for (const index of sequence(1, configuration.numCases + 1)) {
      sockets.push({
        key: `${index}`,
        valueType: 'flow'
      });
    }
    return sockets;
  },
  initialState: undefined,
  triggered: ({
    read,
    commit,
    configuration
  }) => {
    const selection = read('selection');
    for (const index of sequence(1, configuration.numCases + 1)) {
      if (selection === read(`${index}`)) {
        commit(`${index}`);
        return;
      }
    }
    commit('default');
  }
});

// https://docs.unrealengine.com/4.27/en-US/ProgrammingAndScripting/Blueprints/UserGuide/flow/

const SwitchOnString = makeFlowNodeDefinition({
  typeName: 'flow/switch/string',
  label: 'Switch on String',
  configuration: {
    numCases: {
      valueType: 'number'
    }
  },
  in: configuration => {
    const sockets = [];
    sockets.push({
      key: 'flow',
      valueType: 'flow'
    }, {
      key: 'selection',
      valueType: 'string'
    });
    for (const index of sequence(1, configuration.numCases + 1)) {
      sockets.push({
        key: `${index}`,
        valueType: 'string'
      });
    }
    return sockets;
  },
  out: configuration => {
    const sockets = [];
    sockets.push({
      key: 'default',
      valueType: 'flow'
    });
    for (const index of sequence(1, configuration.numCases + 1)) {
      sockets.push({
        key: `${index}`,
        valueType: 'flow'
      });
    }
    return sockets;
  },
  initialState: undefined,
  triggered: ({
    read,
    commit,
    configuration
  }) => {
    const selection = read('selection');
    for (const index of sequence(1, configuration.numCases + 1)) {
      if (selection === read(`${index}`)) {
        commit(`${index}`);
        return;
      }
    }
    commit('default');
  }
});

const Easing = makeFunctionNodeDefinition({
  typeName: 'math/easing',
  category: NodeCategory.Logic,
  label: 'Easing',
  in: {
    easingFunction: {
      valueType: 'string',
      name: 'easingFunction',
      defaultValue: 'linear',
      options: Object.keys(EasingFunctions)
    },
    easingMode: {
      valueType: 'string',
      name: 'easingMode',
      defaultValue: 'inOut',
      options: Object.keys(EasingModes)
    },
    t: 'float'
  },
  out: {
    t: 'float'
  },
  exec: ({
    read,
    write
  }) => {
    const easingFunction = EasingFunctions[read('easingFunction')];
    const easingMode = EasingModes[read('easingMode')];
    const easing = easingMode(easingFunction);
    const inputT = read('t');
    write('t', easing(inputT));
  }
});

// Unreal Engine Blueprint Time nodes: https://docs.unrealengine.com/4.27/en-US/BlueprintAPI/Utilities/Time/

const Now = makeInNOutFunctionDesc({
  name: 'time/now',
  label: 'Now',
  out: 'float',
  exec: () => Date.now() / 1000
});

var TimeNodes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Now: Now
});

/* eslint-disable max-len */
const makeCoreDependencies = ({
  lifecyleEmitter,
  logger
}) => ({
  [lifecycleEventEmitterDependencyKey]: lifecyleEmitter,
  [loggerDependencyKey]: logger
});
function getCoreValueTypes() {
  return toMap([BooleanValue, StringValue, IntegerValue, FloatValue], v => v.name);
}
function toMap(elements, getName) {
  return Object.fromEntries(elements.map(element => [getName(element), element]));
}
function getStringConversions(values) {
  return ['boolean', 'float', 'integer'].flatMap(valueTypeName => getStringConversionsForValueType({
    values,
    valueTypeName
  }));
}
function getCoreNodeDefinitions(values) {
  const allNodeDefinitions = [...getNodeDescriptions(StringNodes), ...getNodeDescriptions(BooleanNodes), ...getNodeDescriptions(IntegerNodes), ...getNodeDescriptions(FloatNodes),
  // custom events

  OnCustomEvent.Description, TriggerCustomEvent.Description,
  // variables

  VariableGet, VariableSet,
  // complex logic

  Easing,
  // actions

  Log, ExpectTrue.Description,
  // events

  LifecycleOnStart, LifecycleOnEnd, LifecycleOnTick,
  // time

  Delay.Description, ...getNodeDescriptions(TimeNodes),
  // flow control

  Branch, FlipFlop, ForLoop, Sequence, SwitchOnInteger, SwitchOnString, Debounce.Description, Throttle.Description, DoN, DoOnce, Gate, MultiGate, WaitAll.Description, Counter, ...getStringConversions(values)];

  // convert array to map
  return toMap(allNodeDefinitions, node => node.typeName);
}
const getCoreRegistry = () => {
  const values = getCoreValueTypes();
  return {
    values,
    nodes: getCoreNodeDefinitions(getCoreValueTypes())
  };
};

export { Assert, AsyncNode, AsyncNode2, AsyncNodeInstance, BooleanNodes, BooleanValue, Branch, Counter, CustomEvent, Debounce, DefaultLogger, Delay, DoN, DoOnce, EPSILON, EasingFunctions, EasingModes, Engine, EventEmitter, EventNode, EventNode2, EventNodeInstance, ExpectTrue, Fiber, FlipFlop, FloatNodes, FloatValue, FlowNode, FlowNode2, FlowNodeInstance, ForLoop, FunctionNode, FunctionNodeInstance, Gate, IntegerNodes, IntegerValue, LifecycleOnEnd, LifecycleOnStart, LifecycleOnTick, Link, Log, Logger, ManualLifecycleEventEmitter, MultiGate, Node, NodeCategory, NodeDescription, NodeDescription2, NodeType, OnCustomEvent, Sequence, Socket, StringNodes, StringValue, Throttle, TriggerCustomEvent, ValueType, Variable, VariableGet, VariableSet, WaitAll, clamp, createNode, degreesToRadians, equalsAutoTolerance, equalsTolerance, getCoreNodeDefinitions, getCoreRegistry, getCoreValueTypes, getNodeDescriptions, getStringConversionsForValueType, isAsyncNode, isEventNode, isFlowNode, isFunctionNode, lifecycleEventEmitterDependencyKey, loggerDependencyKey, makeAsyncNodeDefinition, makeCoreDependencies, makeEventNodeDefinition, makeFlowNodeDefinition, makeFunctionNodeDefinition, makeGraphApi, makeInNOutFunctionDesc, makeNodeInstance, parseSafeFloat, parseSafeFloats, radiansToDegrees, readGraphFromJSON, sequence, sleep, toCamelCase, toMap, toSafeString, validateGraph, validateGraphAcyclic, validateGraphLinks, validateNodeRegistry, validateRegistry, validateValueRegistry, writeGraphToJSON, writeNodeSpecsToJSON };
